import { storageKey, writeLocalJson, removeLocalKey, readLocalJsonOptional } from '@authe/fe-commons/storage';
import { parseAbi, toEventSelector, http, fallback, defineChain, numberToHex, isAddress, maxUint256, getAddress, parseUnits, zeroAddress, recoverMessageAddress, isAddressEqual, verifyMessage as verifyMessage$1, erc721Abi, decodeEventLog, decodeFunctionData, encodeFunctionData, erc20Abi, erc1155Abi, ethAddress, formatUnits, hashTypedData, hashDomain, getTypesForEIP712Domain, createPublicClient, decodeFunctionResult, serializeTransaction, formatTransactionRequest, pad } from 'viem';
export { getAddress, isAddress, isAddressEqual } from 'viem';
import { ChainFamily, registry, isActiveChain, getChainMetadata, toHexChainId, normalizeAddress, chainSelectOptions, chainDisplayName } from '@authe/fe-commons/chains';
import { WagmiProvider, createConfig, injected, usePublicClient, useWalletClient, useSignMessage, useAccount, useChainId, useChains, useSwitchChain, useConnect, useDisconnect } from 'wagmi';
import { createLogger } from '@authe/fe-commons/logging';
import { blockArg, chainIdToNumber } from '@authe/fe-commons/contracts';
import * as React10 from 'react';
import { TwoKeySigner } from '@authe/fe-commons/signing';
import { verifyMessage } from 'viem/actions';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { useChainRegistryVersion, Select, SelectTrigger, SelectValue, cn, SelectContent, SelectItem, ChainAvatar, Badge, AddressDisplay, Button } from '@authe/ui-core';
import { isUnlimitedAllowance } from '@authe/fe-commons/value';
import { estimateGas } from 'viem/linea';

// src/config.ts
var WEB3_PACKAGE = "web3";
var WEB3_SELECTED_CHAIN_KEY = storageKey(WEB3_PACKAGE, "chain");
var WEB3_EVM_CONNECTION_ID = "evm:wagmi";
var WEB3_DEFAULT_CHAIN_SLUG = "eth";
var WEB3_SIGNER_TTL_MS = 15 * 6e4;
var WEB3_SIGNER_ROOT_ID = "authe-web3";
var FLAGS_KEY = storageKey(WEB3_PACKAGE, "disconnected");
function readFlags() {
  return readLocalJsonOptional(FLAGS_KEY) ?? {};
}
function markDisconnected(id) {
  const flags = readFlags();
  if (flags[id]) return;
  flags[id] = true;
  writeLocalJson(FLAGS_KEY, flags);
}
function clearDisconnected(id) {
  const flags = readFlags();
  if (!flags[id]) return;
  delete flags[id];
  if (Object.keys(flags).length === 0) removeLocalKey(FLAGS_KEY);
  else writeLocalJson(FLAGS_KEY, flags);
}
function isDisconnected(id) {
  return readFlags()[id] === true;
}
function resolveRpcUrls(chain, overrides) {
  if (overrides) {
    if (chain.chainIdHex && overrides[chain.chainIdHex]?.length) return overrides[chain.chainIdHex];
    if (overrides[chain.slug]?.length) return overrides[chain.slug];
  }
  return [...chain.rpcUrls ?? []];
}
function rpcTransport(urls) {
  if (urls.length <= 1) return http(urls[0]);
  return fallback(urls.map((url) => http(url)));
}
function toViemChain(chain, options = {}) {
  if (chain.family !== ChainFamily.EVM || !chain.chainIdHex) return null;
  let id;
  try {
    id = Number(BigInt(chain.chainIdHex));
  } catch {
    return null;
  }
  if (!Number.isSafeInteger(id) || id <= 0) return null;
  const urls = options.rpcUrls ?? resolveRpcUrls(chain, options.overrides);
  if (!urls.length) return null;
  return defineChain({
    id,
    name: chain.name,
    nativeCurrency: { name: chain.nativeToken, symbol: chain.nativeToken, decimals: chain.decimals },
    rpcUrls: { default: { http: urls } },
    ...chain.explorerUrl ? { blockExplorers: { default: { name: `${chain.name} Explorer`, url: chain.explorerUrl } } } : {},
    testnet: chain.isTestnet
  });
}
function autheEvmViemChains(options = {}) {
  const wanted = options.slugs ? new Set(options.slugs) : null;
  const out = [];
  for (const chain of registry.all()) {
    if (chain.family !== ChainFamily.EVM) continue;
    if (!isActiveChain(chain)) continue;
    if (!options.includeTestnets && chain.isTestnet) continue;
    if (wanted && !wanted.has(chain.slug)) continue;
    const viem = toViemChain(chain, { overrides: options.overrides });
    if (!viem) continue;
    if (chain.slug === WEB3_DEFAULT_CHAIN_SLUG) out.unshift(viem);
    else out.push(viem);
  }
  return out;
}
var log = createLogger(`${WEB3_PACKAGE}:wagmi`);
function createAutheWagmiConfig(options = {}) {
  const resolved = options.chains ?? autheEvmViemChains({
    includeTestnets: options.includeTestnets,
    overrides: options.rpc,
    slugs: options.slugs
  });
  if (resolved.length === 0) {
    throw new Error(
      "createAutheWagmiConfig: no EVM chains resolved from the registry \u2014 supply `rpc` overrides or explicit `chains`."
    );
  }
  const chains = resolved;
  const transports = Object.fromEntries(
    chains.map((c) => [c.id, options.transports?.[c.id] ?? rpcTransport(c.rpcUrls.default.http)])
  );
  log.debug("creating wagmi config", { chains: chains.map((c) => c.id) });
  return createConfig({
    chains,
    connectors: options.connectors ?? [injected()],
    transports,
    ssr: options.ssr
  });
}

// src/contract/keys.ts
function stableArgsKey(args) {
  function encode(value) {
    if (value === null) return ["null"];
    if (Array.isArray(value)) return ["array", value.map(encode)];
    if (typeof value === "object") {
      return ["object", Object.keys(value).sort().map((key) => [key, encode(value[key])])];
    }
    return [typeof value, String(value)];
  }
  return JSON.stringify(encode(args ?? []));
}
function createRpcPublicClients(rpcOverrides) {
  const clients = /* @__PURE__ */ new Map();
  return (chainId) => {
    const meta = getChainMetadata(chainId);
    if (!meta) throw new Error(`Unknown chain: ${chainId}`);
    const chain = toViemChain(meta, { overrides: rpcOverrides });
    if (!chain) throw new Error(`No EVM RPC endpoint for ${meta.name}.`);
    const key = String(chain.id);
    let client = clients.get(key);
    if (!client) {
      client = createPublicClient({ chain, transport: rpcTransport(chain.rpcUrls.default.http) });
      clients.set(key, client);
    }
    return client;
  };
}

// src/contract/reader.ts
var DEFAULT_LOG_WINDOW = 1000n;
function toError(reason) {
  return reason instanceof Error ? reason : new Error(String(reason));
}
function createRpcContractReader(options = {}) {
  const clientFor = options.clientFor ?? createRpcPublicClients(options.rpcOverrides);
  const stateBlock = (ref) => options.blockHash ? { blockHash: options.blockHash, requireCanonical: true } : blockArg(ref.block);
  return {
    id: "rpc",
    withBlockHash: (blockHash) => createRpcContractReader({ ...options, clientFor, blockHash }),
    async read(ref, req) {
      const client = clientFor(ref.chainId);
      return client.readContract({
        address: ref.address,
        abi: req.abi,
        functionName: req.functionName,
        args: req.args,
        ...stateBlock(ref)
      });
    },
    async readMany(ref, reqs) {
      if (reqs.length === 0) return [];
      const client = clientFor(ref.chainId);
      const settled = await Promise.allSettled(
        reqs.map(
          (r) => client.readContract({
            address: ref.address,
            abi: r.abi,
            functionName: r.functionName,
            args: r.args,
            ...stateBlock(ref)
          })
        )
      );
      return settled.map(
        (s) => s.status === "fulfilled" ? { status: "success", data: s.value } : { status: "failure", error: toError(s.reason) }
      );
    },
    async call(ref, data) {
      const client = clientFor(ref.chainId);
      const result = await client.call({ to: ref.address, data, ...stateBlock(ref) });
      return result.data ?? "0x";
    },
    async getStorageAt(ref, slot) {
      const client = clientFor(ref.chainId);
      return client.getStorageAt({ address: ref.address, slot, ...stateBlock(ref) });
    },
    async resolveBlockNumber(ref) {
      if (ref.block?.blockNumber != null) return ref.block.blockNumber;
      if (ref.block?.blockTag === "pending") throw new Error("Choose a mined block to inspect proxy storage.");
      const block = await clientFor(ref.chainId).getBlock({ blockTag: ref.block?.blockTag ?? "latest" });
      if (block.number == null) throw new Error("The selected block has not been mined.");
      return block.number;
    },
    async getNonce(ref) {
      const block = options.blockHash ? { blockHash: options.blockHash, requireCanonical: true } : ref.block?.blockNumber != null ? numberToHex(ref.block.blockNumber) : ref.block?.blockTag ?? "latest";
      const value = await clientFor(ref.chainId).request({ method: "eth_getTransactionCount", params: [ref.address, block] });
      return BigInt(value);
    },
    async getBytecode(ref) {
      return await clientFor(ref.chainId).getBytecode({ address: ref.address, ...stateBlock(ref) }) ?? "0x";
    },
    async getBlockIdentity(ref) {
      const block = await clientFor(ref.chainId).getBlock(blockArg(ref.block));
      if (block.number == null || !block.hash) throw new Error("Select a mined block to collect evidence.");
      return { number: block.number, hash: block.hash };
    },
    async getLogs(ref, query = {}, opts) {
      opts?.signal?.throwIfAborted();
      const client = clientFor(ref.chainId);
      const window2 = query.lookback ?? options.logWindow ?? DEFAULT_LOG_WINDOW;
      const toBlock = query.toBlock ?? ref.block?.blockNumber ?? ref.block?.blockTag ?? "latest";
      let fromBlock = query.fromBlock;
      if (fromBlock === void 0) {
        const head = typeof toBlock === "bigint" ? toBlock : toBlock === "earliest" ? 0n : toBlock === "latest" || toBlock === "pending" ? await client.getBlockNumber() : (await client.getBlock({ blockTag: toBlock })).number;
        fromBlock = head > window2 ? head - window2 : 0n;
      }
      if (typeof fromBlock === "bigint" && fromBlock < 0n || typeof toBlock === "bigint" && toBlock < 0n) {
        throw new Error("Block heights must be non-negative.");
      }
      if (typeof fromBlock === "bigint" && typeof toBlock === "bigint" && fromBlock > toBlock) {
        throw new Error("From block must not be after To block.");
      }
      if (query.topics?.length || query.indexedTopics || query.chainWide) {
        const logs2 = await client.request({ method: "eth_getLogs", params: [{
          ...query.chainWide ? {} : { address: ref.address },
          fromBlock: typeof fromBlock === "bigint" ? numberToHex(fromBlock) : fromBlock,
          toBlock: typeof toBlock === "bigint" ? numberToHex(toBlock) : toBlock,
          topics: query.indexedTopics ? query.indexedTopics.map((topic) => Array.isArray(topic) ? [...topic] : topic) : query.topics ? [query.topics] : void 0
        }] });
        opts?.signal?.throwIfAborted();
        if (logs2.some((log7) => log7.removed)) throw new Error("The log range includes removed events. Retry after the reorganization.");
        return logs2.map((log7) => ({
          address: log7.address,
          topics: log7.topics,
          data: log7.data,
          blockNumber: log7.blockNumber == null ? null : BigInt(log7.blockNumber),
          logIndex: log7.logIndex == null ? null : Number(BigInt(log7.logIndex)),
          transactionHash: log7.transactionHash
        }));
      }
      const logs = await client.getLogs({ address: ref.address, fromBlock, toBlock });
      return logs.map((log7) => ({
        address: log7.address,
        topics: log7.topics,
        data: log7.data,
        blockNumber: log7.blockNumber,
        logIndex: log7.logIndex,
        transactionHash: log7.transactionHash
      }));
    }
  };
}

// src/contract/readSession.ts
function createContractReadSession(reader, chainId, blockNumber, signal) {
  const reads = /* @__PURE__ */ new Map();
  return (address2, request) => {
    signal?.throwIfAborted();
    const key = stableArgsKey([address2.toLowerCase(), request.functionName, request.args, request.abi]);
    let result = reads.get(key);
    if (!result) {
      result = reader.read({ chainId, address: address2, block: { blockNumber } }, request, { signal });
      if (reads.size < 512) reads.set(key, result);
    }
    return result.then((value) => {
      signal?.throwIfAborted();
      return value;
    });
  };
}

// src/contract/logHistory.ts
async function readLogRange(reader, ref, query, signal) {
  if (!reader.getLogs) throw new Error("This reader does not support event history.");
  let fromBlock = query.fromBlock;
  const toBlock = query.toBlock;
  if (fromBlock < 0n || toBlock < fromBlock) throw new Error("Invalid event history range.");
  const halveRange = () => {
    const span = (toBlock - fromBlock + 1n) / 2n || 1n;
    fromBlock = toBlock - span + 1n;
  };
  for (; ; ) {
    signal?.throwIfAborted();
    try {
      const logs = await reader.getLogs(ref, { ...query, fromBlock, toBlock }, { signal });
      signal?.throwIfAborted();
      if (logs.length > 3e3 && fromBlock < toBlock) {
        halveRange();
        continue;
      }
      if (logs.some((log7) => log7.blockNumber == null || log7.logIndex == null || log7.blockNumber < fromBlock || log7.blockNumber > toBlock || !Number.isSafeInteger(log7.logIndex) || log7.logIndex < 0 || !query.chainWide && log7.address.toLowerCase() !== ref.address.toLowerCase())) {
        throw new Error("Event history returned invalid event positions or addresses.");
      }
      return { logs, fromBlock, toBlock };
    } catch (error) {
      signal?.throwIfAborted();
      const message = error instanceof Error ? error.message : String(error);
      if (fromBlock < toBlock && /too many (?:results|logs)|(?:block|query) range|response (?:size|too large)|limit.*\d+.*blocks/i.test(message)) {
        halveRange();
        continue;
      }
      throw error;
    }
  }
}
function blockOverride(selector) {
  if (selector.blockNumber != null) return { blockNumber: selector.blockNumber };
  if (selector.blockTag) return { blockTag: selector.blockTag };
  return {};
}
function useEvmRead(params) {
  const { chainId, blockNumber, blockTag, ready, deps, read, onFailure } = params;
  const client = usePublicClient(chainId != null ? { chainId } : void 0);
  const [nonce, setNonce] = React10.useState(0);
  const enabled = ready && Boolean(client);
  const depsKey = stableArgsKey(deps);
  const request = React10.useMemo(
    () => ({ enabled, client, chainId, blockNumber, blockTag, nonce, depsKey }),
    [enabled, client, chainId, blockNumber, blockTag, nonce, depsKey]
  );
  const [result, setResult] = React10.useState(null);
  React10.useEffect(() => {
    if (!enabled || !client) return void 0;
    let cancelled = false;
    Promise.resolve().then(() => read(client, blockOverride({ blockNumber, blockTag }))).then((result2) => {
      if (cancelled) return;
      setResult({ request, data: result2, error: null });
    }).catch((err) => {
      if (cancelled) return;
      if (onFailure) {
        onFailure.log.warn(onFailure.message, { ...onFailure.context?.(), error: String(err) });
      }
      setResult({ request, error: err instanceof Error ? err : new Error(String(err)) });
    });
    return () => {
      cancelled = true;
    };
  }, [request]);
  const refetch = React10.useCallback(() => setNonce((n) => n + 1), []);
  const current = enabled && result?.request === request ? result : null;
  return { data: current?.data, error: current?.error ?? null, isPending: enabled && !current, refetch };
}
var log2 = createLogger(`${WEB3_PACKAGE}:contract-read`);
function useContractRead(params) {
  const { address: address2, abi, functionName, args, chainId, blockNumber, blockTag, enabled = true } = params;
  const argsKey = stableArgsKey(args);
  return useEvmRead({
    chainId,
    blockNumber,
    blockTag,
    ready: enabled && Boolean(address2 && abi && functionName),
    deps: [address2, functionName, argsKey, stableArgsKey(abi)],
    read: async (client, block) => await client.readContract({
      address: address2,
      abi,
      functionName,
      args,
      ...block
    }),
    onFailure: { log: log2, message: "contract read failed", context: () => ({ address: address2, functionName }) }
  });
}
var log3 = createLogger(`${WEB3_PACKAGE}:contract-reads`);
function useContractReads(params) {
  const { calls, chainId, blockNumber, blockTag, enabled = true } = params;
  const callsKey = React10.useMemo(
    () => stableArgsKey(calls),
    [calls]
  );
  const { data, isPending, refetch } = useEvmRead({
    chainId,
    blockNumber,
    blockTag,
    ready: enabled && calls.length > 0,
    deps: [callsKey],
    read: async (client, block) => {
      const settled = await Promise.allSettled(
        calls.map(
          (c) => client.readContract({
            address: c.address,
            abi: c.abi,
            functionName: c.functionName,
            args: c.args,
            ...block
          })
        )
      );
      return settled.map(
        (s) => s.status === "fulfilled" ? { status: "success", data: s.value } : { status: "failure", error: s.reason instanceof Error ? s.reason : new Error(String(s.reason)) }
      );
    },
    onFailure: { log: log3, message: "batch read failed" }
  });
  return { results: data ?? [], isPending, refetch };
}
var PREVIEW_MAX_AGE_MS = 6e4;
var MAX_PREVIEW_DATA_BYTES = 65536;
function validatePreview(request) {
  const chain = getChainMetadata(request.chainId);
  const id = chain?.chainIdHex ? chainIdToNumber(request.chainId) : void 0;
  if (id == null) throw new Error("Choose a registered EVM network for this transaction.");
  if (!isAddress(request.from) || request.to != null && !isAddress(request.to)) throw new Error("Enter valid EVM addresses, including correct mixed-case checksums.");
  if (!/^0x(?:[0-9a-fA-F]{2})*$/.test(request.data) || request.data.length > 2 + MAX_PREVIEW_DATA_BYTES * 2) {
    throw new Error("Calldata must be even-length hex, up to 64 KiB.");
  }
  if (typeof request.value !== "bigint" || request.value < 0n || request.value > maxUint256) {
    throw new Error("Native value must be a non-negative uint256 amount in base units.");
  }
  if (request.to == null && request.data === "0x" && !request.calls) throw new Error("Contract creation requires initcode.");
  let calls;
  if (request.calls) {
    if (!request.calls.length || request.calls.length > 16) throw new Error("A bundle must contain between 1 and 16 calls.");
    calls = request.calls.map((call) => {
      if (!call.to) throw new Error("Bundle calls need recipient addresses.");
      const checked = validatePreview({ ...call, chainId: request.chainId, from: request.from });
      return { to: checked.to, data: checked.data, value: checked.value };
    });
    if (calls.reduce((sum, call) => sum + call.value, 0n) > maxUint256) throw new Error("Bundle value exceeds uint256.");
  }
  return { chainId: `eip155:${id}`, from: getAddress(request.from), to: request.to ? getAddress(request.to) : void 0, data: request.data, value: request.value, ...calls ? { calls } : {} };
}
function preparePreviewRequest(draft) {
  const decimals = getChainMetadata(draft.chainId)?.decimals;
  if (decimals == null) throw new Error("The network's native token decimals are unavailable.");
  const value = draft.nativeValue.trim();
  if (value.length > 335 || !/^\d+(?:\.\d+)?$/.test(value) || (value.split(".")[1]?.length ?? 0) > decimals) {
    throw new Error(`Native value must be non-negative with at most ${decimals} decimals.`);
  }
  return validatePreview({
    chainId: draft.chainId,
    from: draft.from.trim(),
    to: draft.creation ? void 0 : draft.to.trim(),
    data: draft.data.trim(),
    value: parseUnits(value, decimals)
  });
}
var previewRequestKey = (r) => [chainIdToNumber(r.chainId) ?? r.chainId, r.from.toLowerCase(), r.to?.toLowerCase() ?? "create", r.data.toLowerCase(), r.value.toString(), ...(r.calls ?? []).flatMap((call) => [call.to.toLowerCase(), call.data.toLowerCase(), call.value.toString()])].join(":");
function previewCanSend(result, request, now, acceptUnavailable) {
  return Boolean(result && result.fees?.affordable !== false && previewRequestKey(result.request) === previewRequestKey(request) && Number.isFinite(result.checkedAt) && now - result.checkedAt >= 0 && now - result.checkedAt < PREVIEW_MAX_AGE_MS && (result.status === "success" || result.status === "unavailable" && acceptUnavailable));
}
function assertReviewedTransaction(request, authorization) {
  authorization.signal?.throwIfAborted();
  validatePreview(request);
  if (!previewCanSend(authorization.preview, request, Date.now(), authorization.acceptUnavailable)) {
    throw new Error("The preview expired, failed or no longer matches this transaction. Preview again before signing.");
  }
}
var previewCalls = (request) => request.calls ?? [{ to: request.to, data: request.data, value: request.value }];
function prepareBundleRequest(json, chainId, from) {
  if (json.length > MAX_PREVIEW_DATA_BYTES * 2) throw new Error("Bundle JSON must be at most 128 KiB.");
  const raw = JSON.parse(json);
  if (!Array.isArray(raw) || !raw.length || raw.length > 16) throw new Error("Provide an array of 1\u201316 calls.");
  const calls = raw.map((call) => {
    if (!call || typeof call !== "object" || typeof call.value !== "string" || !/^(?:0x[0-9a-fA-F]+|\d+)$/.test(call.value)) throw new Error("Each call needs to, data and value as a decimal or hex string in base units.");
    return { to: call.to, data: call.data, value: BigInt(call.value) };
  });
  return validatePreview({ chainId, from, to: calls[0].to, data: "0x", value: 0n, calls });
}

// src/contract/signingAccount.ts
async function assertSigningAccount(wallet, chainId, owner) {
  const [chain, accounts] = await Promise.all([wallet.getChainId(), wallet.getAddresses()]);
  if (chainId == null || chain !== chainId || wallet.chain?.id !== chainId || wallet.account.address.toLowerCase() !== owner.toLowerCase() || accounts[0]?.toLowerCase() !== owner.toLowerCase()) {
    throw new Error("Wallet account or network changed. Review again before signing.");
  }
}

// src/contract/useContractWrite.ts
var log4 = createLogger(`${WEB3_PACKAGE}:contract-write`);
function writeOutcome(receipt) {
  if (!receipt) return "sent";
  return receipt.status === "reverted" ? "reverted" : "success";
}
function useContractWrite(chainId) {
  const { data: walletClient, error: walletError } = useWalletClient(chainId != null ? { chainId } : void 0);
  const publicClient = usePublicClient(chainId != null ? { chainId } : void 0);
  const [bundleId, setBundleId] = React10.useState(null);
  const [hash, setHash] = React10.useState(null);
  const [receipt, setReceipt] = React10.useState(null);
  const [status, setStatus] = React10.useState("idle");
  const [error, setError] = React10.useState(null);
  const attemptRef = React10.useRef(0);
  const inFlight = React10.useRef(false);
  React10.useEffect(() => () => {
    attemptRef.current += 1;
  }, []);
  const reset = React10.useCallback(() => {
    attemptRef.current += 1;
    setBundleId(null);
    setHash(null);
    setReceipt(null);
    setStatus("idle");
    setError(null);
  }, []);
  const write = React10.useCallback(
    async (call, value, expectedAccount, beforeSend) => {
      if (inFlight.current) return null;
      const attempt = ++attemptRef.current;
      const current = () => attempt === attemptRef.current;
      let submittedHash = null;
      setBundleId(null);
      setHash(null);
      setReceipt(null);
      setError(null);
      if (!walletClient) {
        const err = walletError ?? new Error("No connected wallet to sign the transaction.");
        setError(err);
        setStatus("error");
        return null;
      }
      inFlight.current = true;
      setStatus("signing");
      try {
        if (beforeSend) await beforeSend(false);
        const checkWallet = async () => {
          if (expectedAccount) await assertSigningAccount(walletClient, chainId, expectedAccount);
        };
        await checkWallet();
        if (!current()) throw new Error("The transaction review was closed or reset before signing.");
        if (beforeSend) await beforeSend(true);
        await checkWallet();
        if (beforeSend) await beforeSend(false);
        if (!current()) throw new Error("The transaction review was closed or reset before signing.");
        if (call.bundle) {
          const caps = await walletClient.getCapabilities({ account: walletClient.account.address, chainId });
          if (caps?.atomic?.status !== "supported" && caps?.atomic?.status !== "ready") throw new Error("This wallet does not support atomic call bundles on this network.");
          await checkWallet();
          if (beforeSend) await beforeSend(true);
          await checkWallet();
          if (beforeSend) await beforeSend(false);
          if (!current()) throw new Error("The bundle review was closed before signing.");
          const sent = await walletClient.sendCalls({ account: walletClient.account, chain: walletClient.chain, calls: call.bundle, forceAtomic: true, experimental_fallback: false });
          if (typeof sent.id !== "string" || !sent.id.length || sent.id.length > 8194) throw new Error("The wallet returned an invalid bundle identifier.");
          if (current()) {
            setBundleId(sent.id);
            setStatus("pending");
          }
          const outcome = await walletClient.waitForCallsStatus({ id: sent.id, throwOnFailure: true });
          submittedHash = outcome.receipts?.[0]?.transactionHash ?? null;
          if (current()) setHash(submittedHash);
          if (outcome.chainId !== chainId || outcome.id !== sent.id) throw new Error("The wallet returned confirmation for a different bundle or network.");
          if (outcome.status !== "success" || outcome.atomic !== true || !outcome.receipts?.length || !outcome.receipts.every((r2) => r2.status === "success")) throw new Error("The wallet did not confirm atomic execution of every call.");
          if (!publicClient) {
            if (current()) setStatus("sent");
            return submittedHash;
          }
          if (await publicClient.getChainId() !== chainId) throw new Error("The confirmation RPC is on a different network.");
          for (const reported of outcome.receipts) {
            const confirmed = await publicClient.waitForTransactionReceipt({ hash: reported.transactionHash });
            if (confirmed.status !== "success" || confirmed.transactionHash !== reported.transactionHash || confirmed.blockHash !== reported.blockHash || confirmed.blockNumber !== reported.blockNumber) throw new Error("The wallet bundle receipt could not be confirmed on this network.");
            if (current()) setReceipt(confirmed);
          }
          if (current()) setStatus("success");
          return submittedHash;
        }
        const txHash = call.data ? await walletClient.sendTransaction({
          to: call.creation ? void 0 : call.address,
          data: call.data,
          account: walletClient.account,
          chain: walletClient.chain,
          ...value != null ? { value } : {}
        }) : await walletClient.writeContract({
          address: call.address,
          abi: call.abi,
          functionName: call.functionName,
          args: call.args,
          account: walletClient.account,
          chain: walletClient.chain,
          ...value != null ? { value } : {}
        });
        submittedHash = txHash;
        if (current()) {
          setHash(txHash);
          setStatus("pending");
        }
        if (!publicClient) {
          if (current()) setStatus(writeOutcome(null));
          return txHash;
        }
        let replacementReason;
        const r = await publicClient.waitForTransactionReceipt({
          hash: txHash,
          onReplaced: ({ reason }) => {
            replacementReason = reason;
          }
        });
        submittedHash = r.transactionHash;
        if (!current()) return submittedHash;
        setHash(submittedHash);
        setReceipt(r);
        if (replacementReason === "cancelled" || replacementReason === "replaced") {
          setError(new Error(replacementReason === "cancelled" ? "Transaction was cancelled in the wallet." : "Transaction was replaced by a different transaction; the requested call was not confirmed."));
          setStatus("error");
          return submittedHash;
        }
        if (writeOutcome(r) === "reverted") {
          log4.warn("contract write reverted on chain", {
            address: call.address,
            functionName: call.functionName,
            hash: txHash
          });
          setError(
            new Error(
              `Transaction reverted on chain. ${call.functionName} was mined but the contract rejected it, so nothing changed.`
            )
          );
          setStatus("reverted");
          return submittedHash;
        }
        setStatus(writeOutcome(r));
        return submittedHash;
      } catch (err) {
        log4.warn("contract write failed", { address: call.address, functionName: call.functionName, error: String(err) });
        if (current()) {
          setError(err instanceof Error ? err : new Error(String(err)));
          setStatus("error");
        }
        return submittedHash;
      } finally {
        inFlight.current = false;
      }
    },
    [walletClient, walletError, publicClient, chainId]
  );
  const writeReviewed = React10.useCallback(async (input, authorization, call) => {
    const request = validatePreview(input);
    const beforeSend = async (readyToSend) => {
      assertReviewedTransaction(request, authorization);
      if (chainIdToNumber(request.chainId) !== chainId) throw new Error("The reviewed network differs from the signing network.");
      if (readyToSend) {
        if (!publicClient) throw new Error("The preview's block cannot be verified. Reconnect to this network and preview again.");
        const block = await publicClient.getBlock({ blockNumber: authorization.preview.blockNumber });
        if (block.hash !== authorization.preview.blockHash) throw new Error("The preview's block changed. Preview again before signing.");
      }
      assertReviewedTransaction(request, authorization);
    };
    return write(
      { ...call, address: request.to ?? zeroAddress, creation: !request.to && !request.calls, bundle: request.calls, abi: call?.abi ?? [], functionName: call?.functionName ?? "reviewed transaction", data: request.data },
      request.value,
      request.from,
      beforeSend
    );
  }, [chainId, publicClient, write]);
  return { write, writeReviewed, bundleId, hash, receipt, status, error, reset };
}
var log5 = createLogger(`${WEB3_PACKAGE}:storage`);
function useEvmStorageAt(params) {
  const { address: address2, slot, chainId, blockNumber, blockTag, enabled = true } = params;
  return useEvmRead({
    chainId,
    blockNumber,
    blockTag,
    ready: enabled && Boolean(address2 && slot),
    deps: [address2, slot],
    read: async (client, block) => await client.getStorageAt({
      address: address2,
      slot,
      ...block
    }),
    onFailure: { log: log5, message: "storage read failed", context: () => ({ address: address2, slot }) }
  });
}
var log6 = createLogger(`${WEB3_PACKAGE}:signer`);
function useAutheSigner(options = {}) {
  const { rootKeyId = WEB3_SIGNER_ROOT_ID, ttlMs = WEB3_SIGNER_TTL_MS, enabled = true } = options;
  const request = React10.useMemo(() => ({ enabled, rootKeyId, ttlMs }), [enabled, rootKeyId, ttlMs]);
  const [state, setState] = React10.useState(null);
  const current = enabled && state?.request === request ? state : null;
  const signer = current?.signer ?? null;
  const error = current?.error ?? null;
  React10.useEffect(() => {
    if (!enabled) return;
    let active = true;
    void TwoKeySigner.create({ rootKeyId, intermediateTtlMs: ttlMs }).then((created) => {
      if (active) setState({ request, signer: created, error: null });
    }).catch((err) => {
      if (!active) return;
      log6.error("two-key signer creation failed", { error: String(err) });
      setState({ request, signer: null, error: err instanceof Error ? err : new Error(String(err)) });
    });
    return () => {
      active = false;
    };
  }, [enabled, rootKeyId, ttlMs, request]);
  const notReady = React10.useCallback(
    () => Promise.reject(
      error ? new Error(`Authe signer is unavailable: ${error.message}`) : new Error("Authe signer is not ready yet.")
    ),
    [error]
  );
  const signRequest = React10.useCallback(
    (payload) => signer ? signer.signRequest(payload) : notReady(),
    [signer, notReady]
  );
  const exportRootPublicJwk = React10.useCallback(
    () => signer ? signer.exportRootPublicJwk() : notReady(),
    [signer, notReady]
  );
  return { signer, ready: signer !== null, error, signRequest, exportRootPublicJwk };
}
function useEvmSignMessage() {
  const { signMessageAsync, isPending, error } = useSignMessage();
  const { address: account } = useAccount();
  const publicClient = usePublicClient();
  const signMessage = React10.useCallback(
    (message) => signMessageAsync({ message }),
    [signMessageAsync]
  );
  const signAndRecover = React10.useCallback(
    async (message) => {
      const signature = await signMessageAsync({ message });
      const signer = await recoverMessageAddress({ message, signature });
      return {
        signature,
        signer,
        matchesAccount: account ? isAddressEqual(signer, account) : null
      };
    },
    [signMessageAsync, account]
  );
  const signAndVerify = React10.useCallback(
    async (message) => {
      const signature = await signMessageAsync({ message });
      if (!account) {
        return { signature, verdict: { valid: false, method: "unknown", reason: "needs-client" } };
      }
      const verdict = await verifyEvmSignature(account, message, signature, publicClient);
      return { signature, verdict };
    },
    [signMessageAsync, account, publicClient]
  );
  return {
    signMessage,
    signAndRecover,
    signAndVerify,
    isPending,
    error: error ?? null
  };
}
function recoverSigner(message, signature) {
  return recoverMessageAddress({ message, signature });
}
async function verifyEvmSignature(address2, message, signature, client) {
  if (client) {
    const valid2 = await verifyMessage(client, { address: address2, message, signature });
    return { valid: valid2, method: "erc1271" };
  }
  const valid = await verifyMessage$1({ address: address2, message, signature });
  if (valid) return { valid: true, method: "eoa" };
  return { valid: false, method: "unknown", reason: "needs-client" };
}
var ContentContext = React10.createContext(null);
function Content() {
  return React10.useContext(ContentContext);
}
var WalletBoundary = React10.memo(function WalletBoundary2({ config, reconnectOnMount }) {
  return /* @__PURE__ */ jsx(WagmiProvider, { config, reconnectOnMount, children: /* @__PURE__ */ jsx(Content, {}) });
});
function Web3Provider({
  config,
  queryClient,
  reconnectOnMount,
  children
}) {
  const client = React10.useMemo(() => queryClient ?? new QueryClient(), [queryClient]);
  const [resolvedReconnect] = React10.useState(
    () => reconnectOnMount ?? !isDisconnected(WEB3_EVM_CONNECTION_ID)
  );
  return /* @__PURE__ */ jsx(QueryClientProvider, { client, children: /* @__PURE__ */ jsx(ContentContext.Provider, { value: children, children: /* @__PURE__ */ jsx(WalletBoundary, { config, reconnectOnMount: resolvedReconnect }) }) });
}
function persistSlug(slug) {
  if (slug) writeLocalJson(WEB3_SELECTED_CHAIN_KEY, slug);
}
function useSelectedChain() {
  const configuredChainId = useChainId();
  const account = useAccount();
  const chains = useChains();
  const chainId = account.isConnected && account.chainId != null ? account.chainId : configuredChainId;
  const { switchChain, isPending, error } = useSwitchChain();
  const chain = getChainMetadata(String(chainId));
  const requestSwitch = React10.useCallback(
    (id, slug) => {
      switchChain({ chainId: id }, { onSuccess: () => persistSlug(slug) });
    },
    [switchChain]
  );
  const switchToChainId = React10.useCallback(
    (id) => requestSwitch(id, getChainMetadata(String(id))?.slug),
    [requestSwitch]
  );
  const switchToSlug = React10.useCallback(
    (slug) => {
      const hex = toHexChainId(slug);
      if (!hex) return false;
      const id = Number(BigInt(hex));
      if (!Number.isSafeInteger(id) || !chains.some((configured) => configured.id === id)) return false;
      requestSwitch(id, getChainMetadata(slug)?.slug);
      return true;
    },
    [requestSwitch, chains]
  );
  return {
    chainId,
    chain,
    isSupported: chains.some((configured) => configured.id === chainId),
    isSwitching: isPending,
    switchError: error ?? null,
    switchToChainId,
    switchToSlug
  };
}
function useAddressInventory() {
  const { address: address2, addresses, chainId, isConnected, status, connector } = useAccount();
  const entries = React10.useMemo(() => {
    if (!isConnected) return [];
    const list = addresses ?? (address2 ? [address2] : []);
    const activeLower = address2?.toLowerCase();
    return list.map((a) => ({
      address: a,
      normalized: normalizeAddress(a),
      isActive: a.toLowerCase() === activeLower
    }));
  }, [addresses, address2, isConnected]);
  const active = React10.useMemo(() => entries.find((e) => e.isActive) ?? null, [entries]);
  const chain = chainId != null ? getChainMetadata(String(chainId)) : null;
  return { entries, active, chainId, chain, isConnected, status, connector: connector?.name };
}

// src/wallet/connect-recovery.ts
function watchStuckWalletConnect(onStuck) {
  if (typeof document === "undefined" || typeof window === "undefined") return () => void 0;
  let timer;
  const schedule = () => {
    if (timer !== void 0) clearTimeout(timer);
    timer = setTimeout(onStuck, 400);
  };
  const onVisibility = () => {
    if (document.visibilityState === "visible") schedule();
  };
  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("focus", schedule);
  return () => {
    if (timer !== void 0) clearTimeout(timer);
    document.removeEventListener("visibilitychange", onVisibility);
    window.removeEventListener("focus", schedule);
  };
}

// src/wallet/connection-error.ts
function walletConnectionError(error, wallet = "your wallet") {
  let cause = error;
  for (let depth = 0; cause && typeof cause === "object" && depth < 8; depth += 1) {
    const rpc = cause;
    if (rpc.code === 4001) return new Error("Connection request rejected. Click Connect to try again.");
    if (rpc.code === -32002) return new Error(`A connection request is already open in ${wallet}. Open the extension to approve or reject it, then click Connect again.`);
    cause = rpc.cause;
  }
  return error instanceof Error ? error : new Error("Connection failed. Please try again.");
}

// src/react/useWalletConnection.ts
function useWalletConnection() {
  const { address: address2, isConnected } = useAccount();
  const { connectAsync, connectors: registered, reset } = useConnect();
  const connectors = React10.useMemo(() => registered.some((connector) => connector.type === "injected" && connector.id !== "injected") ? registered.filter((connector) => connector.id !== "injected") : registered, [registered]);
  const { disconnect } = useDisconnect();
  const [isConnecting, setIsConnecting] = React10.useState(false);
  const [error, setError] = React10.useState(null);
  const [selectedConnector, setSelectedConnector] = React10.useState();
  const attemptRef = React10.useRef(0);
  React10.useEffect(() => () => {
    attemptRef.current += 1;
  }, []);
  const clearConnecting = React10.useCallback(() => {
    setIsConnecting(false);
    reset();
  }, [reset]);
  const doConnect = React10.useCallback(
    (connector) => {
      const target = connector ?? (connectors.length === 1 ? connectors[0] : void 0);
      if (!connector && connectors.length > 1) {
        setError(new Error("Choose which wallet to connect."));
        return;
      }
      if (!target) {
        setError(new Error("No wallet connector is available."));
        return;
      }
      setSelectedConnector(target);
      const attempt = ++attemptRef.current;
      reset();
      clearDisconnected(WEB3_EVM_CONNECTION_ID);
      setIsConnecting(true);
      setError(null);
      void (async () => {
        try {
          await connectAsync({ connector: target });
          if (attempt !== attemptRef.current && isDisconnected(WEB3_EVM_CONNECTION_ID)) {
            disconnect({ connector: target });
          }
        } catch (e) {
          if (attempt !== attemptRef.current) return;
          setError(walletConnectionError(e, target.name));
          reset();
        } finally {
          if (attempt === attemptRef.current) setIsConnecting(false);
        }
      })();
    },
    [connectAsync, connectors, reset, disconnect]
  );
  React10.useEffect(() => {
    if (!isConnecting) return;
    return watchStuckWalletConnect(() => {
      if (isConnected) return;
      setError((prev) => prev ?? new Error("Connection cancelled"));
      attemptRef.current += 1;
      clearConnecting();
    });
  }, [isConnecting, isConnected, clearConnecting]);
  const doDisconnect = React10.useCallback(() => {
    attemptRef.current += 1;
    clearConnecting();
    setError(null);
    markDisconnected(WEB3_EVM_CONNECTION_ID);
    disconnect();
  }, [disconnect, clearConnecting]);
  return {
    isConnected,
    address: address2,
    connectors,
    connect: doConnect,
    disconnect: doDisconnect,
    isConnecting,
    error,
    selectedConnector
  };
}

// src/wallet/injected.ts
function injected2() {
  return (typeof window !== "undefined" ? window : {}) ?? {};
}
var getEvmProvider = () => injected2().ethereum;
var getSolanaProvider = () => injected2().solana;
var getBitcoinProvider = () => injected2().unisat;

// src/wallet/adapters.ts
function createInjectedEvmAdapter(options = {}) {
  const getProvider = options.getProvider ?? getEvmProvider;
  const toAccounts = (addrs, chainId) => {
    const chain = chainId ? getChainMetadata(chainId)?.slug ?? chainId : void 0;
    return addrs.map((a) => ({ address: a.toLowerCase(), family: ChainFamily.EVM, chain }));
  };
  return {
    id: options.id ?? "injected-evm",
    name: options.name ?? "Browser wallet",
    family: ChainFamily.EVM,
    isAvailable: () => Boolean(getProvider()),
    async connect() {
      const provider = getProvider();
      if (!provider) throw new Error("No EVM wallet found");
      const addrs = await provider.request({ method: "eth_requestAccounts" });
      const chainId = await provider.request({ method: "eth_chainId" }).catch(() => void 0);
      return toAccounts(addrs ?? [], chainId);
    },
    async getAccounts() {
      const provider = getProvider();
      if (!provider) return [];
      const addrs = await provider.request({ method: "eth_accounts" }).catch(() => []);
      const chainId = await provider.request({ method: "eth_chainId" }).catch(() => void 0);
      return toAccounts(addrs ?? [], typeof chainId === "string" ? chainId : void 0);
    },
    async disconnect() {
    },
    subscribe(listener) {
      const provider = getProvider();
      if (!provider?.on || !provider.removeListener) return () => void 0;
      let revision = 0;
      const refresh = async (addrs, chainId) => {
        const request = ++revision;
        const [accounts, chain] = await Promise.all([
          addrs ?? provider.request({ method: "eth_accounts" }).catch(() => []),
          chainId ?? provider.request({ method: "eth_chainId" }).catch(() => void 0)
        ]);
        if (request === revision) listener(toAccounts(accounts, typeof chain === "string" ? chain : void 0));
      };
      const onAccounts = (addrs) => {
        void refresh(addrs);
      };
      const onChain = (chainId) => {
        void refresh(void 0, chainId);
      };
      const onDisconnect = () => {
        revision += 1;
        listener([]);
      };
      provider.on("accountsChanged", onAccounts);
      provider.on("chainChanged", onChain);
      provider.on("disconnect", onDisconnect);
      return () => {
        revision += 1;
        provider.removeListener?.("accountsChanged", onAccounts);
        provider.removeListener?.("chainChanged", onChain);
        provider.removeListener?.("disconnect", onDisconnect);
      };
    }
  };
}
function createSolanaAdapter(options = {}) {
  const getProvider = options.getProvider ?? getSolanaProvider;
  const account = (address2) => ({ address: address2, family: ChainFamily.SOLANA, chain: "solana" });
  return {
    id: options.id ?? "phantom",
    name: options.name ?? "Phantom",
    family: ChainFamily.SOLANA,
    isAvailable: () => Boolean(getProvider()),
    async connect() {
      const provider = getProvider();
      if (!provider) throw new Error("No Solana wallet found");
      const res = await provider.connect();
      return [account(res.publicKey.toString())];
    },
    async getAccounts() {
      const pk = getProvider()?.publicKey;
      return pk ? [account(pk.toString())] : [];
    },
    async disconnect() {
      await getProvider()?.disconnect();
    },
    subscribe(listener) {
      const provider = getProvider();
      if (!provider?.on || !provider.removeListener) return () => void 0;
      const onAccount = (key) => listener(key ? [account(key.toString())] : []);
      const onConnect = () => onAccount(provider.publicKey ?? null);
      const onDisconnect = () => listener([]);
      provider.on("accountChanged", onAccount);
      provider.on("connect", onConnect);
      provider.on("disconnect", onDisconnect);
      return () => {
        provider.removeListener?.("accountChanged", onAccount);
        provider.removeListener?.("connect", onConnect);
        provider.removeListener?.("disconnect", onDisconnect);
      };
    }
  };
}
function createBitcoinAdapter(options = {}) {
  const getProvider = options.getProvider ?? getBitcoinProvider;
  const toAccounts = (addrs) => (addrs ?? []).map((a) => ({ address: a, family: ChainFamily.UTXO, chain: "bitcoin" }));
  return {
    id: options.id ?? "unisat",
    name: options.name ?? "Unisat",
    family: ChainFamily.UTXO,
    isAvailable: () => Boolean(getProvider()),
    async connect() {
      const provider = getProvider();
      if (!provider) throw new Error("No Bitcoin wallet found");
      return toAccounts(await provider.requestAccounts());
    },
    async getAccounts() {
      const provider = getProvider();
      if (!provider) return [];
      return toAccounts(await provider.getAccounts().catch(() => []));
    },
    async disconnect() {
    },
    subscribe(listener) {
      const provider = getProvider();
      if (!provider?.on || !provider.removeListener) return () => void 0;
      const onAccounts = (addrs) => listener(toAccounts(addrs));
      provider.on("accountsChanged", onAccounts);
      return () => provider.removeListener?.("accountsChanged", onAccounts);
    }
  };
}
function defaultWalletAdapters() {
  return [createInjectedEvmAdapter(), createSolanaAdapter(), createBitcoinAdapter()];
}
var WalletsContext = React10.createContext(null);
function WalletProvider({ adapters, autoConnect = true, children }) {
  const adapterList = React10.useMemo(() => adapters ?? defaultWalletAdapters(), [adapters]);
  const value = useWalletHubState(adapterList, autoConnect);
  return /* @__PURE__ */ jsx(WalletsContext.Provider, { value, children });
}
function useWallets() {
  const ctx = React10.useContext(WalletsContext);
  if (!ctx) throw new Error("useWallets must be used within a <WalletProvider>");
  return ctx;
}
function useWalletHubState(adapters, autoConnect) {
  const [snapshot, setSnapshot] = React10.useState(/* @__PURE__ */ new Map());
  const operations = React10.useRef(/* @__PURE__ */ new Map());
  const watchers = React10.useRef(/* @__PURE__ */ new Map());
  const listening = React10.useRef(/* @__PURE__ */ new Set());
  const liveAdapters = React10.useRef(/* @__PURE__ */ new Map());
  const byId = React10.useMemo(() => new Map(adapters.map((a) => [a.id, a])), [adapters]);
  const resolve = React10.useCallback(
    (target) => {
      const adapter = byId.get(typeof target === "string" ? target : target.id);
      return typeof target === "string" || adapter === target ? adapter : void 0;
    },
    [byId]
  );
  const begin = React10.useCallback((adapter) => {
    watchers.current.get(adapter)?.();
    watchers.current.delete(adapter);
    const operation = {};
    operations.current.set(adapter, operation);
    return () => liveAdapters.current.has(adapter) && operations.current.get(adapter) === operation;
  }, []);
  const update = React10.useCallback((adapter, state) => {
    setSnapshot((prev) => new Map(prev).set(adapter, state));
  }, []);
  const applyAccounts = React10.useCallback((adapter, accounts) => {
    update(adapter, { accounts, status: accounts.length ? "connected" : "disconnected" });
  }, [update]);
  const remove = React10.useCallback((adapter) => {
    liveAdapters.current.get(adapter)?.unsubscribe?.();
    liveAdapters.current.delete(adapter);
    operations.current.delete(adapter);
    listening.current.delete(adapter);
    watchers.current.get(adapter)?.();
    watchers.current.delete(adapter);
  }, []);
  React10.useEffect(() => {
    const live = liveAdapters.current;
    return () => {
      for (const adapter of live.keys()) remove(adapter);
    };
  }, [remove]);
  React10.useEffect(() => {
    const next = new Set(adapters);
    const removed = [...liveAdapters.current.keys()].filter((adapter) => !next.has(adapter));
    for (const adapter of removed) remove(adapter);
    if (removed.length) setSnapshot((prev) => {
      const states = new Map(prev);
      for (const adapter of removed) states.delete(adapter);
      return states;
    });
    for (const adapter of adapters) {
      if (!adapter.isAvailable()) continue;
      let entry = liveAdapters.current.get(adapter);
      if (!entry) {
        entry = { hydrated: false };
        liveAdapters.current.set(adapter, entry);
        entry.unsubscribe = adapter.subscribe?.((accounts) => {
          if (!liveAdapters.current.has(adapter) || !listening.current.has(adapter)) return;
          begin(adapter);
          applyAccounts(adapter, accounts);
        });
      }
      if (autoConnect && !entry.hydrated && !listening.current.has(adapter) && !isDisconnected(adapter.id)) {
        entry.hydrated = true;
        listening.current.add(adapter);
        const current = begin(adapter);
        void adapter.getAccounts().then((accounts) => {
          if (current() && listening.current.has(adapter)) applyAccounts(adapter, accounts);
        }).catch(() => void 0);
      }
    }
  }, [adapters, autoConnect, applyAccounts, begin, remove]);
  const connect = React10.useCallback(async (target) => {
    const adapter = resolve(target);
    if (!adapter || !liveAdapters.current.has(adapter)) return;
    liveAdapters.current.get(adapter).hydrated = true;
    const current = begin(adapter);
    listening.current.add(adapter);
    clearDisconnected(adapter.id);
    update(adapter, { accounts: [], status: "connecting" });
    const stopWatch = watchStuckWalletConnect(() => {
      if (!current()) return;
      begin(adapter);
      listening.current.delete(adapter);
      update(adapter, { accounts: [], status: "disconnected", error: "Connection cancelled" });
    });
    watchers.current.set(adapter, stopWatch);
    try {
      const accounts = await adapter.connect();
      if (current()) applyAccounts(adapter, accounts);
    } catch (error) {
      if (current()) {
        listening.current.delete(adapter);
        update(adapter, { accounts: [], status: "error", error: walletConnectionError(error, adapter.name).message });
      }
    } finally {
      stopWatch();
      if (current()) watchers.current.delete(adapter);
    }
  }, [resolve, begin, update, applyAccounts]);
  const disconnect = React10.useCallback(async (target) => {
    const adapter = resolve(target);
    if (!adapter || !liveAdapters.current.has(adapter)) return;
    const current = begin(adapter);
    listening.current.delete(adapter);
    markDisconnected(adapter.id);
    applyAccounts(adapter, []);
    try {
      await adapter.disconnect();
    } catch (error) {
      if (current()) update(adapter, {
        accounts: [],
        status: "disconnected",
        error: error instanceof Error ? error.message : "Disconnect failed"
      });
    }
  }, [resolve, begin, applyAccounts, update]);
  return React10.useMemo(() => {
    const states = new Map(adapters.map((adapter) => [adapter.id, snapshot.get(adapter)]));
    const accountsByAdapter = {};
    const accountsByFamily = {};
    for (const adapter of adapters) {
      const accounts2 = states.get(adapter.id)?.accounts ?? [];
      accountsByAdapter[adapter.id] = accounts2;
      (accountsByFamily[adapter.family] ??= []).push(...accounts2);
    }
    const accounts = Object.values(accountsByAdapter).flat();
    return {
      adapters,
      available: adapters.filter((adapter) => adapter.isAvailable()),
      accounts,
      accountsByAdapter,
      accountsByFamily,
      status: (id) => states.get(id)?.status ?? "disconnected",
      error: (id) => states.get(id)?.error,
      connect,
      disconnect,
      isConnected: (family) => family ? (accountsByFamily[family]?.length ?? 0) > 0 : accounts.length > 0
    };
  }, [adapters, snapshot, connect, disconnect]);
}
function ChainPicker({
  slugs,
  includeTestnets = false,
  placeholder = "Select chain",
  className
}) {
  const { chain, chainId, switchToSlug, switchError, isSwitching } = useSelectedChain();
  const chains = useChains();
  const errorId = React10.useId();
  const registryVersion = useChainRegistryVersion();
  const options = React10.useMemo(() => {
    const wanted = slugs ? new Set(slugs) : null;
    const configured = new Set(chains.map((item) => getChainMetadata(String(item.id))?.slug));
    const all = chainSelectOptions({ valueKind: "slug", evmOnly: true, includeTestnets }).filter((option) => configured.has(option.slug));
    return wanted ? all.filter((option) => wanted.has(option.slug)) : all;
  }, [slugs, includeTestnets, chains, registryVersion]);
  const selection = options.find((option) => option.slug === chain?.slug);
  const currentName = chain?.name ?? chains.find((configured) => configured.id === chainId)?.name ?? `Unsupported network (${chainId})`;
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1", children: [
    /* @__PURE__ */ jsxs(
      Select,
      {
        value: selection?.slug ?? "",
        onValueChange: (slug) => switchToSlug(slug),
        disabled: isSwitching || options.length === 0,
        children: [
          /* @__PURE__ */ jsx(
            SelectTrigger,
            {
              width: "lg",
              className: cn("", className),
              "aria-label": "Wallet network",
              "aria-describedby": switchError ? errorId : void 0,
              "aria-invalid": switchError ? true : void 0,
              children: /* @__PURE__ */ jsx(SelectValue, { placeholder: selection ? placeholder : currentName })
            }
          ),
          /* @__PURE__ */ jsx(SelectContent, { children: options.map((option) => /* @__PURE__ */ jsx(SelectItem, { value: option.value, children: /* @__PURE__ */ jsx(ChainAvatar, { chain: option.slug, size: "sm" }) }, option.value)) })
        ]
      }
    ),
    switchError && /* @__PURE__ */ jsxs("p", { id: errorId, role: "alert", className: "max-w-[200px] text-2xs text-destructive", children: [
      "Could not switch chain: ",
      switchError.message
    ] })
  ] });
}
var FAMILY_LABEL = {
  evm: "EVM",
  utxo: "Bitcoin",
  solana: "Solana",
  cosmos: "Cosmos",
  xrpl: "XRP Ledger",
  tron: "TRON",
  ton: "TON",
  near: "NEAR",
  aptos: "Aptos",
  sui: "Sui",
  starknet: "Starknet",
  antelope: "Antelope",
  hypercore: "Hyperliquid",
  corda: "Corda",
  fabric: "Hyperledger Fabric",
  canton: "Canton",
  other: "Other"
};
function WalletMenu({ onlyAvailable = false, className }) {
  const { adapters, accountsByAdapter, status, error, connect, disconnect } = useWallets();
  const rows = onlyAvailable ? adapters.filter((a) => a.isAvailable()) : adapters;
  return /* @__PURE__ */ jsx("div", { className: cn("divide-y rounded-md border", className), children: rows.map((adapter) => {
    const available = adapter.isAvailable();
    const account = accountsByAdapter[adapter.id]?.[0];
    const state = status(adapter.id);
    const err = error(adapter.id);
    const connected = state === "connected" && account;
    return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-3 px-3 py-2.5", children: [
      /* @__PURE__ */ jsxs("div", { className: "min-w-0 flex-1", children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx("span", { className: "text-sm font-medium", children: adapter.name }),
          /* @__PURE__ */ jsx(Badge, { variant: "outline", className: "text-3xs", children: FAMILY_LABEL[adapter.family] })
        ] }),
        connected ? /* @__PURE__ */ jsx("div", { className: "font-mono text-xs text-muted-foreground", children: /* @__PURE__ */ jsx(AddressDisplay, { address: account.address, chain: account.chain }) }) : err ? /* @__PURE__ */ jsx("p", { role: "alert", className: "break-words text-xs text-destructive", children: err }) : !available ? /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "Not installed" }) : null
      ] }),
      connected ? /* @__PURE__ */ jsx(Button, { type: "button", size: "sm", variant: "outline", "aria-label": `Disconnect ${adapter.name}`, onClick: () => void disconnect(adapter), children: "Disconnect" }) : /* @__PURE__ */ jsx(
        Button,
        {
          type: "button",
          size: "sm",
          "aria-label": `Connect ${adapter.name}`,
          disabled: !available,
          onClick: () => void connect(adapter),
          children: state === "connecting" ? "Connecting\u2026" : "Connect"
        }
      )
    ] }, adapter.id);
  }) });
}
function EvmWalletMenu({ className }) {
  const { connectors, connect, disconnect, isConnected, isConnecting, selectedConnector, error } = useWalletConnection();
  const [detected, setDetected] = React10.useState();
  React10.useEffect(() => {
    let current = true;
    void Promise.all(connectors.map(async (connector) => {
      const available = connector.type !== "injected" || Boolean(await connector.getProvider().catch(() => void 0));
      return available ? connector.uid : void 0;
    })).then((ids) => {
      if (current) setDetected({ connectors, ids: new Set(ids.filter((id) => id !== void 0)) });
    });
    return () => {
      current = false;
    };
  }, [connectors]);
  const ready = detected?.connectors === connectors;
  const wallets = connectors.filter((connector) => detected?.ids.has(connector.uid));
  return /* @__PURE__ */ jsxs("div", { className: cn("space-y-3", className), children: [
    isConnected ? /* @__PURE__ */ jsx(Button, { type: "button", size: "sm", variant: "outline", onClick: disconnect, children: "Disconnect EVM wallet" }) : !ready ? /* @__PURE__ */ jsx("p", { role: "status", className: "text-sm text-muted-foreground", children: "Checking installed wallets\u2026" }) : wallets.length ? /* @__PURE__ */ jsx("ul", { className: "divide-y rounded-md border", "aria-label": "EVM wallets", children: wallets.map((connector) => /* @__PURE__ */ jsxs("li", { className: "flex items-center justify-between gap-3 px-3 py-2.5", children: [
      /* @__PURE__ */ jsx("span", { className: "text-sm font-medium", children: connector.name }),
      /* @__PURE__ */ jsx(Button, { type: "button", size: "sm", "aria-label": `Connect ${connector.name}`, onClick: () => connect(connector), children: isConnecting && selectedConnector?.uid === connector.uid ? "Retry connection" : "Connect" })
    ] }, connector.uid)) }) : /* @__PURE__ */ jsx("p", { className: "text-sm text-muted-foreground", children: "No EVM wallet detected. Enable MetaMask or another EVM wallet extension in this browser, then reload this page." }),
    isConnecting && /* @__PURE__ */ jsxs("p", { role: "status", className: "text-sm text-muted-foreground", children: [
      "Open ",
      selectedConnector?.name ?? "your wallet",
      " to approve or reject the connection request."
    ] }),
    error && /* @__PURE__ */ jsx("p", { role: "alert", className: "break-words text-sm text-destructive", children: error.message })
  ] });
}

// src/contract/blockAtTime.ts
async function blockAtTime(client, timestamp, head, signal) {
  if (timestamp < 0n || head.number == null || timestamp > head.timestamp) throw new Error("Choose a time no later than the latest mined block.");
  let low = 0n, high = head.number;
  let found = await client.getBlock({ blockNumber: low });
  if (found.timestamp > timestamp) throw new Error("The selected time predates this chain.");
  for (let calls = 0; low <= high; calls++) {
    signal?.throwIfAborted();
    if (calls >= 64) throw new Error("Historical block lookup exceeded its bounded search.");
    const mid = (low + high) / 2n;
    const block = mid === head.number ? head : await client.getBlock({ blockNumber: mid });
    if (block.number !== mid || !block.hash) throw new Error("Historical block lookup returned an inconsistent block.");
    if (block.timestamp <= timestamp) {
      found = block;
      low = mid + 1n;
    } else high = mid - 1n;
  }
  return found;
}
var PERMISSION_ABI = parseAbi([
  "event Approval(address indexed owner,address indexed spender,uint256 value)",
  "event ApprovalForAll(address indexed owner,address indexed operator,bool approved)",
  "event Approval(address indexed owner,address indexed token,address indexed spender,uint160 amount,uint48 expiration)",
  "event Permit(address indexed owner,address indexed token,address indexed spender,uint160 amount,uint48 expiration,uint48 nonce)",
  "event Lockdown(address indexed owner,address token,address spender)",
  "function approve(address spender,uint256 amount)",
  "function setApprovalForAll(address operator,bool approved)",
  "function approve(address token,address spender,uint160 amount,uint48 expiration)",
  "function permit(address owner,address spender,uint256 value,uint256 deadline,uint8 v,bytes32 r,bytes32 s)",
  "function allowance(address owner,address token,address spender) view returns (uint160 amount,uint48 expiration,uint48 nonce)",
  "function lockdown((address token,address spender)[] approvals)"
]);
var MAX_UINT160 = (1n << 160n) - 1n;
function permissionExpiry(seconds) {
  const date = new Date(seconds * 1e3);
  return Number.isNaN(date.getTime()) ? `Unix timestamp ${seconds}` : date.toISOString();
}
var approvalId = (row) => [row.kind, row.contract.toLowerCase(), row.spender.toLowerCase(), row.token?.toLowerCase() ?? "", row.tokenId?.toString() ?? ""].join(":");
function permissionFromLog(log7) {
  if (!isAddress(log7.address, { strict: false })) return null;
  try {
    const abi = log7.topics.length === 4 && log7.data === "0x" ? erc721Abi : PERMISSION_ABI;
    const parsed = decodeEventLog({ abi, data: log7.data, topics: log7.topics, strict: true });
    const args = parsed.args;
    const owner = args.owner;
    const spender = args.spender ?? args.operator ?? args.approved;
    if (!isAddress(owner, { strict: false }) || !isAddress(spender, { strict: false })) return null;
    const contract = log7.address;
    if (parsed.eventName === "ApprovalForAll") return { contract, owner, spender, kind: "operator", approved: Boolean(args.approved), unlimited: Boolean(args.approved) };
    if (parsed.eventName === "Lockdown") return { contract, owner, spender, token: args.token, kind: "permit2", amount: 0n, unlimited: false };
    if (args.tokenId != null) return { contract, owner, spender, kind: "nft", tokenId: BigInt(String(args.tokenId)), unlimited: false };
    const amount = BigInt(String(args.amount ?? args.value));
    return {
      contract,
      owner,
      spender,
      kind: args.token ? "permit2" : "amount",
      amount,
      token: args.token,
      expiresAt: args.expiration == null ? void 0 : Number(args.expiration),
      unlimited: isUnlimitedAllowance(amount, args.token ? 160 : void 0)
    };
  } catch {
    return null;
  }
}
function candidateFromPermission(p) {
  return {
    contract: p.contract,
    spender: p.spender,
    token: p.token,
    tokenId: p.tokenId,
    kind: p.kind === "amount" ? "erc20" : p.kind === "nft" ? "erc721" : p.kind
  };
}
function requestedPermissions(request) {
  if (request.calls) return request.calls.flatMap((call) => requestedPermissions({ ...request, ...call, calls: void 0 }));
  if (!request.to) return [];
  const contract = request.to;
  try {
    const { functionName, args } = decodeFunctionData({ abi: PERMISSION_ABI, data: request.data });
    const values = args;
    if (functionName === "lockdown") return values[0].map((pair) => ({
      contract,
      owner: request.from,
      token: pair.token,
      spender: pair.spender,
      kind: "permit2",
      amount: 0n,
      unlimited: false
    }));
    if (functionName === "setApprovalForAll") return [{ contract, owner: request.from, spender: values[0], kind: "operator", approved: values[1], unlimited: values[1] }];
    if (functionName === "approve" && values.length === 4) return [{ contract, owner: request.from, token: values[0], spender: values[1], kind: "permit2", amount: values[2], expiresAt: Number(values[3]), unlimited: isUnlimitedAllowance(values[2], 160) }];
    if (functionName === "approve") return [{ contract, owner: request.from, spender: values[0], kind: "amount", amount: values[1], unlimited: isUnlimitedAllowance(values[1]) }];
    if (functionName === "permit") return [{ contract, owner: values[0], spender: values[1], kind: "amount", amount: values[2], unlimited: isUnlimitedAllowance(values[2]) }];
  } catch {
  }
  return [];
}
function revocationRequest(chainId, owner, row) {
  const data = row.kind === "operator" ? encodeFunctionData({ abi: PERMISSION_ABI, functionName: "setApprovalForAll", args: [row.spender, false] }) : row.kind === "permit2" ? encodeFunctionData({ abi: PERMISSION_ABI, functionName: "lockdown", args: [[{ token: row.token, spender: row.spender }]] }) : row.kind === "erc721" ? encodeFunctionData({ abi: erc721Abi, functionName: "approve", args: [zeroAddress, row.tokenId] }) : encodeFunctionData({ abi: erc20Abi, functionName: "approve", args: [row.spender, 0n] });
  return { chainId, from: owner, to: row.contract, data, value: 0n };
}

// src/safety/assessment.ts
async function readApproval(read, owner, row, timestamp) {
  const base = {
    kind: row.kind,
    contract: row.contract,
    spender: row.spender,
    token: row.token,
    tokenId: row.tokenId,
    id: approvalId(row),
    status: "unknown",
    unlimited: false
  };
  const uint = (value) => {
    if (typeof value !== "bigint" || value < 0n || value >= 1n << 256n) throw new Error("The contract returned an invalid unsigned amount.");
    return value;
  };
  try {
    if (row.kind === "operator") {
      const active = await read(row.contract, { abi: erc1155Abi, functionName: "isApprovedForAll", args: [owner, row.spender] });
      if (typeof active !== "boolean") throw new Error("The operator permission could not be established.");
      return { ...base, status: active ? "active" : "inactive", unlimited: active };
    }
    if (row.kind === "erc721") {
      const [heldBy, approved] = await Promise.all([
        read(row.contract, { abi: erc721Abi, functionName: "ownerOf", args: [row.tokenId] }),
        read(row.contract, { abi: erc721Abi, functionName: "getApproved", args: [row.tokenId] })
      ]);
      if (typeof heldBy !== "string" || !isAddress(heldBy) || typeof approved !== "string" || !isAddress(approved)) throw new Error("The NFT ownership or permission could not be established.");
      return { ...base, tokenOwner: heldBy, approvedSpender: approved, status: heldBy.toLowerCase() === owner.toLowerCase() && approved.toLowerCase() === row.spender.toLowerCase() && BigInt(approved) !== 0n ? "active" : "inactive" };
    }
    const token = row.token ?? row.contract;
    const approval = row.kind === "permit2" ? await read(row.contract, { abi: PERMISSION_ABI, functionName: "allowance", args: [owner, token, row.spender] }) : [await read(token, { abi: erc20Abi, functionName: "allowance", args: [owner, row.spender] })];
    if (!Array.isArray(approval)) throw new Error("The allowance could not be established.");
    const amount = uint(approval[0]);
    const expiresAt = row.kind === "permit2" ? Number(approval[1]) : void 0;
    if (expiresAt != null && (!Number.isSafeInteger(expiresAt) || expiresAt < 0 || expiresAt >= 2 ** 48)) throw new Error("The permission expiration could not be established.");
    const expired = expiresAt != null && BigInt(expiresAt) < timestamp;
    const meta = await Promise.allSettled([
      read(token, { abi: erc20Abi, functionName: "balanceOf", args: [owner] }).then(uint),
      read(token, { abi: erc20Abi, functionName: "decimals" }),
      read(token, { abi: erc20Abi, functionName: "symbol" }),
      ...row.kind === "permit2" ? [read(token, { abi: erc20Abi, functionName: "allowance", args: [owner, row.contract] }).then(uint)] : []
    ]);
    const value = (i) => meta[i]?.status === "fulfilled" ? meta[i].value : void 0;
    const balance = typeof value(0) === "bigint" ? value(0) : void 0;
    const underlying = row.kind === "permit2" ? value(3) : amount;
    const effective = underlying == null ? void 0 : amount < underlying ? amount : underlying;
    const inactive = amount === 0n || expired || underlying === 0n;
    const decimals = value(1);
    const symbol = value(2);
    return {
      ...base,
      status: inactive ? "inactive" : underlying == null ? "unknown" : "active",
      allowance: amount,
      balance,
      expiresAt,
      underlyingAllowance: row.kind === "permit2" ? underlying : void 0,
      exposed: inactive ? 0n : effective == null || balance == null ? void 0 : balance < effective ? balance : effective,
      unlimited: isUnlimitedAllowance(amount, row.kind === "permit2" ? 160 : void 0),
      decimals: typeof decimals === "number" && Number.isInteger(decimals) && decimals >= 0 && decimals <= 255 ? decimals : void 0,
      symbol: typeof symbol === "string" ? symbol.replaceAll("\0", "").trim().slice(0, 40) || void 0 : void 0,
      error: !inactive && (balance == null || underlying == null) ? "Current balance or underlying token permission could not be read; exposure is unknown." : void 0
    };
  } catch (error) {
    return { ...base, error: error instanceof Error ? error.message.slice(0, 400) : "The read could not be completed." };
  }
}
function simulationContext(block) {
  if (!block || typeof block !== "object") return void 0;
  const value = block;
  const uint = (n) => typeof n === "bigint" && n >= 0n ? n : typeof n === "string" && /^0x[0-9a-fA-F]+$/.test(n) ? BigInt(n) : void 0;
  const number = uint(value.number), timestamp = uint(value.timestamp), gasLimit = uint(value.gasLimit);
  if (number == null || timestamp == null || gasLimit == null) return void 0;
  return { number, timestamp, gasLimit, baseFeePerGas: uint(value.baseFeePerGas), prevRandao: uint(value.prevRandao ?? value.mixHash), miner: typeof value.miner === "string" && /^0x[0-9a-fA-F]{40}$/.test(value.miner) ? value.miner : void 0 };
}
async function simulateExecution(client, account, calls, blockNumber, context) {
  if (!context && calls.every((call) => call.to)) return client.simulateCalls({ account, calls: calls.map((call) => ({ ...call, to: call.to })), blockNumber, traceTransfers: true, validation: false });
  const blockOverrides = context ? {
    number: numberToHex(context.number),
    time: numberToHex(context.timestamp),
    gasLimit: numberToHex(context.gasLimit),
    ...context.baseFeePerGas != null ? { baseFeePerGas: numberToHex(context.baseFeePerGas) } : {},
    ...context.prevRandao != null ? { prevRandao: numberToHex(context.prevRandao, { size: 32 }) } : {},
    ...context.miner ? { feeRecipient: context.miner } : {}
  } : void 0;
  const blocks = await client.request({ method: "eth_simulateV1", params: [{ blockStateCalls: [{ blockOverrides, calls: calls.map((call) => ({ from: account, ...call, value: numberToHex(call.value ?? 0n) })) }], traceTransfers: true, validation: false }, numberToHex(blockNumber)] });
  const block = context ? blocks.find((block2) => simulationContext(block2)?.number === context.number) : blocks[0];
  return { assetChanges: [], block, results: (block?.calls ?? []).map((call) => ({
    status: call.status === "0x1" ? "success" : "failure",
    gasUsed: BigInt(call.gasUsed),
    data: call.returnData,
    logs: call.logs,
    error: new Error(call.error?.message ?? "Execution reverted")
  })) };
}

// src/safety/errors.ts
function safetyError(error) {
  if (error && typeof error === "object" && "shortMessage" in error && typeof error.shortMessage === "string") return error.shortMessage.slice(0, 400);
  return error instanceof Error ? error.message.slice(0, 400) : "The read could not be completed.";
}

// src/safety/permissionState.ts
var executionKey = (call) => JSON.stringify({ gas: call.gasUsed.toString(), data: call.data.toLowerCase(), logs: (call.logs ?? []).map((log7) => [log7.address.toLowerCase(), log7.data.toLowerCase(), log7.topics.map((topic) => topic.toLowerCase())]) });
async function verifyPermissionStates(client, reader, request, permissions, blockNumber, timestamp, signal, evidence) {
  const unique = new Map(permissions.filter((p) => p.owner).map((p) => [`${p.owner.toLowerCase()}:${approvalId(candidateFromPermission(p))}`, p]));
  const selected = [...unique.values()].slice(0, 60);
  if (!selected.length) return [];
  const probes = /* @__PURE__ */ new Map();
  const key = (address2, req) => `${address2.toLowerCase()}:${encodeFunctionData(req)}`;
  const read = async (address2, req) => {
    signal?.throwIfAborted();
    const id = key(address2, req);
    probes.set(id, { address: address2, req, data: encodeFunctionData(req) });
    return reader.read({ chainId: request.chainId, address: address2, block: { blockNumber } }, req, { signal });
  };
  const before = [];
  for (const permission of selected) before.push(await readApproval(read, permission.owner, candidateFromPermission(permission), timestamp));
  signal?.throwIfAborted();
  const ordered = [...probes.entries()];
  try {
    if (!evidence?.context) throw new Error("The simulation did not return an execution block context; permission state replay cannot be verified.");
    const simulation = await simulateExecution(
      client,
      request.from,
      [...previewCalls(request), ...ordered.map(([, p]) => ({ to: p.address, data: p.data }))],
      blockNumber,
      evidence.context
    );
    signal?.throwIfAborted();
    const replayContext = simulationContext(simulation.block);
    if (replayContext?.number !== evidence.context.number || replayContext?.timestamp !== evidence.context.timestamp) throw new Error("Permission replay used a different execution block. Refresh the preview.");
    if (simulation.results.length !== previewCalls(request).length + ordered.length) throw new Error("Permission verification returned incomplete results.");
    if (simulation.results.slice(0, previewCalls(request).length).some((call) => call.status !== "success")) throw new Error("Permission verification replay failed.");
    if (evidence.calls.length !== previewCalls(request).length || evidence.calls.some((call, i) => executionKey(call) !== executionKey(simulation.results[i]))) throw new Error("Permission replay differs from the reviewed execution. Refresh the preview.");
    const results = new Map(ordered.map(([id], i) => [id, simulation.results[i + previewCalls(request).length]]));
    const postRead = async (address2, req) => {
      signal?.throwIfAborted();
      const result = results.get(key(address2, req));
      if (result?.status !== "success" || !result.data) throw new Error("The simulated permission state could not be read.");
      return decodeFunctionResult({ ...req, data: result.data });
    };
    const verified = [];
    for (const [i, permission] of selected.entries()) {
      const after = await readApproval(postRead, permission.owner, candidateFromPermission(permission), evidence.context.timestamp);
      verified.push({ permission, before: before[i], after, status: before[i].status === "unknown" || after.status === "unknown" ? "unavailable" : "verified" });
    }
    return verified;
  } catch (error) {
    signal?.throwIfAborted();
    return selected.map((permission, i) => ({ permission, before: before[i], status: "unavailable", error: safetyError(error) }));
  }
}
var opOracle = "0x420000000000000000000000000000000000000F";
var opFeesAbi = parseAbi([
  "function getL1Fee(bytes) view returns (uint256)",
  "function getOperatorFee(uint256) view returns (uint256)"
]);
var margin = (gas) => (gas * 120n + 99n) / 100n;
async function estimatePreviewFees(client, request, blockNumber, simulatedGas) {
  const calls = previewCalls(request);
  const chain = getChainMetadata(request.chainId);
  const notes = [];
  let lineaQuoteComplete = false;
  const executionQuote = async () => {
    if (!request.calls && chain?.stack === "linea") {
      try {
        const estimate = await estimateGas(client, { account: request.from, ...calls[0] });
        if (estimate.gasLimit <= 0n || estimate.baseFeePerGas < 0n || estimate.priorityFeePerGas < 0n) throw new Error("Invalid Linea fee quote");
        lineaQuoteComplete = true;
        notes.push("Linea's current transaction-specific quote includes data posting in the priority fee. This fee quote uses the latest state; simulation and balances retain the reviewed block.");
        return [estimate.gasLimit, { maxFeePerGas: estimate.baseFeePerGas + estimate.priorityFeePerGas, maxPriorityFeePerGas: estimate.priorityFeePerGas }];
      } catch {
        notes.push("Linea's transaction-specific fee quote is unavailable; data posting costs remain unverified.");
      }
    }
    return Promise.all([
      request.calls ? simulatedGas == null ? Promise.reject(new Error("Bundle gas unavailable")) : Promise.resolve(simulatedGas) : client.estimateGas({ account: request.from, ...calls[0], blockNumber }),
      client.estimateFeesPerGas()
    ]);
  };
  const [[estimatedGas, quote], nativeBalance, accountCode] = await Promise.all([
    executionQuote(),
    client.getBalance({ address: request.from, blockNumber }),
    client.getCode({ address: request.from, blockNumber }).then((code) => code ?? "0x").catch(() => void 0)
  ]);
  const maxFeePerGas = quote.maxFeePerGas ?? ("gasPrice" in quote ? quote.gasPrice : void 0);
  if (maxFeePerGas == null || estimatedGas < 0n || maxFeePerGas < 0n) throw new Error("Fee quote unavailable");
  const gasLimit = margin(estimatedGas);
  const gasFee = gasLimit * maxFeePerGas;
  let l1DataFee;
  let operatorFee;
  let coverage = "network";
  let dataFeeIncludedInGas = false;
  const walletFeesRequired = Boolean(request.calls || accountCode !== "0x");
  if (walletFeesRequired) {
    coverage = "wallet-required";
    notes.push("The wallet must quote the complete transaction, account validation, data fees and any sponsorship. Simulated call gas alone is not a wallet fee quote. The wallet must also confirm funding; bundled calls can receive funds before spending them.");
  } else if (chain?.stack === "op_stack") {
    const values = await Promise.allSettled([
      (async () => {
        const nonce = await client.getTransactionCount({ address: request.from, blockNumber });
        const common = { ...calls[0], chainId: chainIdToNumber(request.chainId), nonce, gas: gasLimit };
        const serialized = quote.maxFeePerGas != null ? serializeTransaction({ ...common, type: "eip1559", maxFeePerGas, maxPriorityFeePerGas: quote.maxPriorityFeePerGas ?? 0n }) : serializeTransaction({ ...common, type: "legacy", gasPrice: maxFeePerGas });
        return client.readContract({ address: opOracle, abi: opFeesAbi, functionName: "getL1Fee", args: [serialized], blockNumber });
      })(),
      client.readContract({ address: opOracle, abi: opFeesAbi, functionName: "getOperatorFee", args: [gasLimit], blockNumber })
    ]);
    if (values[0].status === "fulfilled") l1DataFee = values[0].value;
    else {
      coverage = "partial";
      notes.push("Rollup data fee is unavailable; the displayed budget is incomplete.");
    }
    if (values[1].status === "fulfilled") operatorFee = values[1].value;
    else {
      coverage = "partial";
      notes.push("Rollup operator fee is unavailable; it has not been assumed to be zero.");
    }
    notes.push("Data and operator fees use this block's oracle. Data fees can change before inclusion and are not capped by maxFeePerGas.");
  } else if (chain?.stack === "scroll") {
    try {
      const nonce = await client.getTransactionCount({ address: request.from, blockNumber });
      const transaction = formatTransactionRequest({ from: request.from, ...calls[0], nonce, gas: gasLimit, ...quote });
      const fee = await client.request({
        method: "scroll_estimateL1DataFee",
        params: [transaction, numberToHex(blockNumber)]
      });
      if (typeof fee !== "string" || !/^0x[0-9a-f]+$/i.test(fee)) throw new Error("Invalid Scroll data fee");
      l1DataFee = BigInt(fee);
      notes.push("Scroll's node estimates the additional data fee using the reviewed block's fee and compression rules. Data fees can change before inclusion.");
    } catch {
      coverage = "partial";
      notes.push("Scroll's data fee estimate is unavailable; the displayed budget is incomplete.");
    }
  } else if (chain?.stack === "linea") {
    dataFeeIncludedInGas = lineaQuoteComplete;
    if (!lineaQuoteComplete) coverage = "partial";
  } else if (chain?.stack === "arbitrum_nitro" || chain?.stack === "zksync") {
    dataFeeIncludedInGas = true;
    notes.push("The RPC gas estimate includes data posting costs; no additional data fee is added.");
  } else if (chain?.layer === "l2" || !chain?.layer) {
    coverage = "partial";
    notes.push("Additional fees for this network have not been verified. Your wallet must confirm the total.");
  }
  const maximumNetworkFee = gasFee + (l1DataFee ?? 0n) + (operatorFee ?? 0n);
  const value = calls.reduce((sum, call) => sum + call.value, 0n);
  const requiredBalance = value + maximumNetworkFee;
  const affordable = walletFeesRequired ? null : nativeBalance < requiredBalance ? false : coverage === "network" ? true : null;
  return { gasLimit, maxFeePerGas, gasFee, l1DataFee, operatorFee, maximumNetworkFee, nativeBalance, requiredBalance, affordable, coverage, dataFeeIncludedInGas, notes };
}
var APPROVAL_SCAN_BLOCKS = 10000n;
var APPROVAL_SCAN_ROWS = 60;
var TOPICS = [
  "Approval(address,address,uint256)",
  "ApprovalForAll(address,address,bool)",
  "Approval(address,address,address,uint160,uint48)",
  "Permit(address,address,address,uint160,uint48,uint48)",
  "Lockdown(address,address,address)"
].map(toEventSelector);
async function discoverApprovalPage(reader, chainId, owner, cursor, signal) {
  if (!reader.getLogs) throw new Error("This reader does not support approval history.");
  if (cursor.version !== 1 || cursor.chainId !== chainId || cursor.owner.toLowerCase() !== owner.toLowerCase() || cursor.nextBlock < 0n || cursor.nextBlock > cursor.upperBlock || cursor.beforeLogIndex != null && (!Number.isSafeInteger(cursor.beforeLogIndex) || cursor.beforeLogIndex < 0)) {
    throw new Error("Approval history cursor does not match this wallet or network.");
  }
  const candidates = /* @__PURE__ */ new Map();
  const floor = cursor.nextBlock >= APPROVAL_SCAN_BLOCKS ? cursor.nextBlock - APPROVAL_SCAN_BLOCKS + 1n : 0n;
  let next = { ...cursor };
  let fromBlock = cursor.nextBlock + 1n;
  let span = 1000n;
  let issue;
  while (next && next.nextBlock >= floor) {
    signal?.throwIfAborted();
    const end = next.nextBlock;
    let start = next.beforeLogIndex != null ? end : end - floor >= span ? end - span + 1n : floor;
    try {
      const page = await readLogRange(reader, { chainId, address: owner }, {
        fromBlock: start,
        toBlock: end,
        chainWide: true,
        indexedTopics: [TOPICS, pad(owner)]
      }, signal);
      const { logs } = page;
      start = page.fromBlock;
      span = end - start + 1n;
      const ordered = [...logs].sort((a, b) => a.blockNumber === b.blockNumber ? b.logIndex - a.logIndex : a.blockNumber > b.blockNumber ? -1 : 1);
      for (const log7 of ordered) {
        if (next?.beforeLogIndex != null && log7.blockNumber === end && log7.logIndex >= next.beforeLogIndex) continue;
        const permission = permissionFromLog({ address: log7.address, data: log7.data, topics: log7.topics });
        if (!permission || permission.owner?.toLowerCase() !== owner.toLowerCase()) continue;
        const row = candidateFromPermission(permission);
        if (!candidates.has(approvalId(row)) && candidates.size >= APPROVAL_SCAN_ROWS) {
          return {
            candidates: [...candidates.values()],
            fromBlock: log7.blockNumber + 1n,
            cursor: { ...cursor, nextBlock: log7.blockNumber, beforeLogIndex: log7.logIndex + 1 },
            issue
          };
        }
        candidates.set(approvalId(row), row);
      }
      fromBlock = start;
      next = start === 0n ? null : { ...cursor, nextBlock: start - 1n, beforeLogIndex: void 0 };
    } catch (error) {
      signal?.throwIfAborted();
      const message = error instanceof Error ? error.message : String(error);
      issue = `Approval history could not be read for blocks ${start}\u2013${end}: ${message.slice(0, 400)}`;
      break;
    }
  }
  return { candidates: [...candidates.values()], fromBlock, cursor: next, issue };
}

// src/safety/source.ts
function aborted(signal) {
  signal?.throwIfAborted();
}
function address(value) {
  if (!isAddress(value)) throw new Error("Enter a valid EVM address, including a correct checksum when mixed-case.");
  return getAddress(value);
}
async function checkedClient(client, chainId) {
  if (await client.getChainId() !== chainIdToNumber(chainId)) throw new Error("The RPC returned a different network. No assessment was made.");
}
async function verifyBlock(client, number, hash) {
  if ((await client.getBlock({ blockNumber: number })).hash !== hash) throw new Error("The chain reorganized during this check. Run the assessment again.");
}
function createWalletSafetySource(clientFor = createRpcPublicClients(), reader = createRpcContractReader({ clientFor }), indexedDiscovery) {
  return {
    async scan(chainId, rawOwner, options = {}) {
      const owner = address(rawOwner);
      const client = clientFor(chainId);
      aborted(options.signal);
      await checkedClient(client, chainId);
      const head = await client.getBlock({ blockTag: "latest" });
      if (head.number == null || !head.hash) throw new Error("No mined block is available.");
      const previous = options.previous;
      if (previous && (previous.chainId !== chainId || previous.owner.toLowerCase() !== owner.toLowerCase())) throw new Error("Previous approval assessment belongs to a different wallet or network.");
      if (previous && previous.assessmentAt !== options.assessmentAt) throw new Error("The historical assessment time changed. Start discovery again.");
      const assessment = previous ? await client.getBlock({ blockNumber: previous.blockNumber }) : options.assessmentAt != null ? await blockAtTime(client, options.assessmentAt, head, options.signal) : head;
      if (assessment.number == null || !assessment.hash || previous && assessment.hash !== previous.blockHash) throw new Error("The approval assessment block changed. Start discovery again.");
      const toBlock = options.cursor?.upperBlock ?? options.toBlock ?? assessment.number;
      if (toBlock < 0n || toBlock > assessment.number) throw new Error("Choose a mined, non-negative history block.");
      if (options.cursor) await verifyBlock(client, options.cursor.upperBlock, options.cursor.upperHash);
      const candidates = /* @__PURE__ */ new Map();
      for (const row of options.candidates ?? []) {
        address(row.contract);
        address(row.spender);
        if (row.token) address(row.token);
        if (row.kind === "permit2" && !row.token) throw new Error("A Permit2 permission needs its token address.");
        if (row.kind === "erc721" && (row.tokenId == null || row.tokenId < 0n)) throw new Error("An NFT permission needs its token ID.");
        candidates.set(approvalId(row), row);
      }
      const issues = [];
      let indexed = previous?.indexed;
      const continuingIndex = Boolean(previous?.indexed?.cursor);
      if (indexedDiscovery && options.discover !== false && (!previous || continuingIndex)) {
        let invalidPage = false;
        try {
          const found = await indexedDiscovery(chainId, owner, previous?.indexed?.cursor ?? void 0, options.signal);
          aborted(options.signal);
          if (found.candidates.length > 100 || found.cursor && (found.cursor.length > 8192 || found.cursor === previous?.indexed?.cursor)) {
            invalidPage = true;
            throw new Error("Invalid indexed approval page.");
          }
          for (const row of found.candidates) {
            if (row.kind !== "erc20") throw new Error("Unexpected indexed permission type.");
            address(row.contract);
            address(row.spender);
            candidates.set(approvalId(row), row);
          }
          indexed = { status: found.cursor ? "partial" : "complete", cursor: found.cursor };
        } catch {
          aborted(options.signal);
          indexed = { status: "unavailable", cursor: invalidPage ? null : previous?.indexed?.cursor ?? null };
          issues.push("Indexed ERC-20 discovery is unavailable. Log history remains available; this is not a complete approval inventory.");
        }
      }
      const page = options.discover === false || previous && !options.cursor && !previous.cursor ? void 0 : await discoverApprovalPage(
        reader,
        chainId,
        owner,
        options.cursor ?? {
          version: 1,
          chainId,
          owner,
          upperBlock: toBlock,
          upperHash: toBlock === head.number ? head.hash : (await client.getBlock({ blockNumber: toBlock })).hash,
          nextBlock: toBlock
        },
        options.signal
      );
      for (const row of page?.candidates ?? []) candidates.set(approvalId(row), row);
      if (page?.issue) issues.push(page.issue);
      const read = createContractReadSession(reader, chainId, assessment.number, options.signal);
      const assessed = new Map(previous?.rows.map((row) => [row.id, row]));
      const rows = [];
      for (const row of candidates.values()) {
        aborted(options.signal);
        rows.push(assessed.get(approvalId(row)) ?? await readApproval(read, owner, row, assessment.timestamp));
      }
      aborted(options.signal);
      await verifyBlock(client, head.number, head.hash);
      await verifyBlock(client, assessment.number, assessment.hash);
      if (options.cursor) await verifyBlock(client, options.cursor.upperBlock, options.cursor.upperHash);
      const cursor = page?.cursor ?? (page ? null : previous?.cursor ?? null);
      return {
        owner,
        chainId,
        assessmentAt: options.assessmentAt,
        blockTimestamp: assessment.timestamp,
        blockNumber: assessment.number,
        blockHash: assessment.hash,
        checkedAt: previous?.checkedAt ?? Date.now(),
        fromBlock: page?.fromBlock ?? previous?.fromBlock ?? toBlock + 1n,
        toBlock,
        cursor,
        nextToBlock: cursor?.nextBlock ?? (indexed?.cursor ? 0n : null),
        rows,
        issues,
        indexed
      };
    },
    async preview(input, signal) {
      const request = validatePreview(input);
      const client = clientFor(request.chainId);
      aborted(signal);
      await checkedClient(client, request.chainId);
      const head = await client.getBlock({ blockTag: "latest" });
      if (head.number == null || !head.hash) throw new Error("No mined block is available.");
      const result = {
        request,
        status: "unavailable",
        blockNumber: head.number,
        blockHash: head.hash,
        checkedAt: Date.now(),
        balanceCoverage: "unavailable",
        balanceChanges: [],
        permissions: [],
        requestedPermissions: requestedPermissions(request),
        limitations: [
          "This is a simulation at a recorded block, not a guarantee of execution or safety. State can change before signing.",
          "Non-standard assets and unsubmitted off-chain signatures may not be discoverable. State probes cover recognized permissions only.",
          "Fee estimates can change and exclude chain-specific data fees when the RPC does not provide them. Your wallet must recheck fees and nonce before sending.",
          "Revoking an allowance does not cancel existing off-chain permit signatures."
        ]
      };
      if (request.calls) result.limitations.push("Bundle execution uses the selected account as sender. Smart-wallet validation, bundler overhead and sponsorship are checked by the wallet; atomic execution is required. The wallet may ask you to activate smart-account features; review any delegation authorization separately.");
      const parameters = {
        account: request.from,
        calls: previewCalls(request).map((call) => ({ ...call, to: call.to })),
        blockNumber: head.number,
        traceTransfers: true,
        validation: false
      };
      let executionEvidence;
      try {
        let simulation;
        try {
          if (!request.to && !request.calls) throw new Error("Creation uses raw simulation without asset probes.");
          simulation = await client.simulateCalls({ ...parameters, traceAssetChanges: true });
          result.balanceCoverage = "measured";
        } catch {
          aborted(signal);
          simulation = await simulateExecution(client, request.from, previewCalls(request), head.number);
          result.limitations.push("Balance changes could not be measured. Only execution and emitted permission events were checked.");
        }
        aborted(signal);
        const calls = simulation.results.slice(0, previewCalls(request).length);
        const call = calls.find((c) => c.status === "failure") ?? calls[0];
        if (calls.length !== previewCalls(request).length) throw new Error("The node returned incomplete bundle results.");
        if (!call) throw new Error("The node returned no simulation result.");
        if (calls.some((item) => item.status !== "success" && item.status !== "failure")) throw new Error("The node returned an unrecognized simulation status.");
        result.status = call.status === "success" ? "success" : "reverted";
        result.gasUsed = calls.reduce((sum, call2) => sum + call2.gasUsed, 0n);
        if (call.status === "failure") result.error = safetyError(call.error);
        else {
          executionEvidence = { context: simulationContext(simulation.block), calls: calls.map((call2) => ({ gasUsed: call2.gasUsed, data: call2.data, logs: call2.logs })) };
          const meta = getChainMetadata(request.chainId);
          result.balanceChanges = simulation.assetChanges.filter((change) => change.value.diff !== 0n).map((change) => ({
            token: change.token.address,
            before: change.value.pre,
            after: change.value.post,
            change: change.value.diff,
            symbol: change.token.address.toLowerCase() === ethAddress.toLowerCase() ? meta?.nativeToken : change.token.symbol?.slice(0, 40),
            decimals: change.token.address.toLowerCase() === ethAddress.toLowerCase() ? meta?.decimals : change.token.decimals
          }));
          result.simulationCalls = calls.map((call2) => ({ gasUsed: call2.gasUsed, logs: (call2.logs ?? []).map((log7) => ({ address: log7.address, data: log7.data, topics: log7.topics })) }));
          result.simulationLogs = result.simulationCalls.flatMap((call2) => call2.logs);
          result.permissions = result.simulationLogs.flatMap((log7) => {
            const permission = permissionFromLog(log7);
            return permission ? [permission] : [];
          });
        }
      } catch (error) {
        aborted(signal);
        result.status = "unavailable";
        result.balanceCoverage = "unavailable";
        result.balanceChanges = [];
        result.permissions = [];
        result.error = safetyError(error);
        result.limitations.push("Simulation is unavailable on this RPC. No balance or permission outcome has been verified.");
      }
      if (result.status === "success") {
        result.permissionStates = await verifyPermissionStates(
          client,
          reader,
          request,
          [...result.permissions, ...result.requestedPermissions],
          head.number,
          head.timestamp,
          signal,
          executionEvidence
        );
        if (result.permissions.length + result.requestedPermissions.length > 60) result.limitations.push("Permission state verification was limited to 60 distinct permission candidates.");
      }
      try {
        result.fees = await estimatePreviewFees(client, request, head.number, result.status === "success" ? result.gasUsed : void 0);
      } catch {
        result.limitations.push("Fee estimate or native balance could not be read; gas affordability is unknown.");
      }
      aborted(signal);
      await verifyBlock(client, head.number, head.hash);
      return result;
    }
  };
}

// src/safety/useApprovalExposure.ts
var EMPTY = { assessment: null, reading: false, error: null, stale: false };
function useApprovalExposure(source, chainId, owner, assessmentAt) {
  const scope = React10.useMemo(() => ({ source, chainId, owner, assessmentAt }), [source, chainId, owner, assessmentAt]);
  const session = React10.useRef({ scope, assessment: null, controller: null });
  const [view, setView] = React10.useState({ scope, data: EMPTY });
  const state = view.scope === scope ? view.data : EMPTY;
  React10.useEffect(() => () => {
    if (session.current.scope === scope) session.current.controller?.abort();
  }, [scope]);
  const run = React10.useCallback(async (mode, candidates) => {
    if (session.current.scope !== scope) session.current = { scope, assessment: null, controller: null };
    const current = session.current;
    const previous = current.assessment;
    if (mode === "refresh" && !previous) return;
    if (mode === "older" && (!previous?.history || previous.scan.nextToBlock == null)) return;
    current.controller?.abort();
    const attempt = new AbortController();
    current.controller = attempt;
    setView({ scope, data: { assessment: previous, reading: true, error: null, stale: Boolean(previous) } });
    try {
      const discovered = mode === "recent" || mode === "older";
      let scan = await source.scan(chainId, owner, {
        assessmentAt,
        discover: discovered,
        candidates: candidates ?? previous?.scan.rows,
        previous: mode === "older" ? previous?.scan : void 0,
        cursor: mode === "older" ? previous?.scan.cursor ?? void 0 : void 0,
        toBlock: mode === "older" ? previous?.scan.nextToBlock ?? void 0 : void 0,
        signal: attempt.signal
      });
      if (attempt.signal.aborted) return;
      if (scan.chainId !== chainId || scan.owner.toLowerCase() !== owner.toLowerCase()) throw new Error("The assessment returned a different wallet or network.");
      if (mode === "refresh" && previous) {
        scan = {
          ...scan,
          fromBlock: previous.scan.fromBlock,
          toBlock: previous.scan.toBlock,
          nextToBlock: previous.scan.nextToBlock,
          cursor: previous.scan.cursor,
          indexed: previous.scan.indexed,
          issues: [.../* @__PURE__ */ new Set([...previous.scan.issues, ...scan.issues])]
        };
      } else if (mode === "older" && previous) {
        scan = { ...scan, fromBlock: scan.fromBlock < previous.scan.fromBlock ? scan.fromBlock : previous.scan.fromBlock, toBlock: previous.scan.toBlock };
      }
      const assessment = { scan, history: mode === "refresh" ? previous.history : discovered };
      current.assessment = assessment;
      setView({ scope, data: { assessment, reading: false, error: null, stale: false } });
    } catch (error) {
      if (!attempt.signal.aborted) setView({ scope, data: { assessment: previous, reading: false, error: safetyError(error), stale: Boolean(previous) } });
    }
  }, [source, chainId, owner, scope, assessmentAt]);
  const refresh = React10.useCallback(() => run("refresh"), [run]);
  return {
    ...state,
    scan: state.assessment?.scan ?? null,
    history: state.assessment?.history ?? false,
    refresh,
    discover: () => run("recent"),
    older: () => run("older"),
    check: (candidates) => run("pairs", candidates)
  };
}
function SafetyAddress({ address: address2, chainId }) {
  return /* @__PURE__ */ jsx("span", { className: "inline-block max-w-full break-all", title: address2, children: /* @__PURE__ */ jsx(AddressDisplay, { address: address2, chain: chainId, copyable: true }) });
}
function PermissionRow({ permission: p, chainId }) {
  return /* @__PURE__ */ jsxs("li", { className: "space-y-1 rounded border p-3", children: [
    /* @__PURE__ */ jsxs("p", { className: "flex flex-wrap items-center gap-2", children: [
      /* @__PURE__ */ jsx("strong", { children: p.kind === "operator" ? p.approved ? "Collection-wide permission: enabled" : "Collection-wide permission: disabled" : p.kind === "nft" ? `NFT #${p.tokenId}` : p.unlimited ? "Unlimited token permission" : `Permission amount: ${p.amount ?? 0n} base units` }),
      p.unlimited && /* @__PURE__ */ jsx(Badge, { variant: "destructive", children: "Unlimited permission" })
    ] }),
    /* @__PURE__ */ jsxs("p", { children: [
      "Spender: ",
      /* @__PURE__ */ jsx(SafetyAddress, { address: p.spender, chainId })
    ] }),
    /* @__PURE__ */ jsxs("p", { children: [
      "Contract: ",
      /* @__PURE__ */ jsx(SafetyAddress, { address: p.contract, chainId })
    ] }),
    p.owner && /* @__PURE__ */ jsxs("p", { children: [
      "Owner: ",
      /* @__PURE__ */ jsx(SafetyAddress, { address: p.owner, chainId })
    ] }),
    p.token && /* @__PURE__ */ jsxs("p", { children: [
      "Token: ",
      /* @__PURE__ */ jsx(SafetyAddress, { address: p.token, chainId })
    ] }),
    p.expiresAt != null && /* @__PURE__ */ jsxs("p", { children: [
      "Expires: ",
      permissionExpiry(p.expiresAt)
    ] })
  ] });
}
function permissionValue(state) {
  if (!state) return "unknown";
  if (state.kind === "erc721") return state.approvedSpender == null ? "unknown" : state.approvedSpender.toLowerCase() === zeroAddress ? "No token-specific approval" : `Approved: ${state.approvedSpender}`;
  if (state.unlimited) return "Unlimited";
  if (state.allowance == null) return state.status;
  return state.decimals == null ? `${state.allowance} base units` : `${formatUnits(state.allowance, state.decimals)} ${state.symbol ?? "tokens"}`;
}
function TransactionPreviewResult({ result }) {
  return /* @__PURE__ */ jsxs("section", { "aria-label": "Transaction preview result", className: "min-w-0 space-y-3 text-sm [overflow-wrap:anywhere]", children: [
    /* @__PURE__ */ jsx("p", { role: "status", className: "font-semibold", children: result.status === "success" ? result.balanceCoverage === "unavailable" ? "Execution simulated \xB7 balance changes unavailable" : "Simulation completed" : result.status === "reverted" ? "Transaction would revert" : "Simulation unavailable" }),
    /* @__PURE__ */ jsxs("p", { className: "text-xs text-muted-foreground", children: [
      "Based on block ",
      result.blockNumber.toString(),
      " \xB7 checked ",
      new Date(result.checkedAt).toISOString(),
      result.gasUsed != null ? ` \xB7 ${result.gasUsed} gas used` : ""
    ] }),
    result.error && /* @__PURE__ */ jsx("p", { role: "alert", className: "break-words text-destructive", children: result.error }),
    result.fees && /* @__PURE__ */ jsxs("div", { className: "rounded border p-3 space-y-1", children: [
      /* @__PURE__ */ jsxs("p", { children: [
        result.fees.coverage === "network" ? "Estimated network fee budget" : result.fees.coverage === "partial" ? "Known network fee subtotal" : result.fees.coverage === "wallet-required" ? "Simulated call fee subtotal" : "Estimated execution fee budget",
        ": ",
        formatUnits(result.fees.maximumNetworkFee, getChainMetadata(result.request.chainId)?.decimals ?? 18),
        " ",
        getChainMetadata(result.request.chainId)?.nativeToken
      ] }),
      result.fees.l1DataFee != null && /* @__PURE__ */ jsxs("p", { children: [
        "Data posting fee: ",
        formatUnits(result.fees.l1DataFee, getChainMetadata(result.request.chainId)?.decimals ?? 18),
        " ",
        getChainMetadata(result.request.chainId)?.nativeToken
      ] }),
      result.fees.operatorFee != null && /* @__PURE__ */ jsxs("p", { children: [
        "Operator fee: ",
        formatUnits(result.fees.operatorFee, getChainMetadata(result.request.chainId)?.decimals ?? 18),
        " ",
        getChainMetadata(result.request.chainId)?.nativeToken
      ] }),
      /* @__PURE__ */ jsxs("p", { className: "text-xs", children: [
        "Gas budget ",
        result.fees.gasLimit.toString(),
        " includes a 20% margin. The wallet confirms the final fee before signing."
      ] }),
      result.fees.notes?.map((note) => /* @__PURE__ */ jsx("p", { className: "text-xs", children: note }, note)),
      result.fees.affordable == null && /* @__PURE__ */ jsx("p", { children: "Gas affordability requires the wallet's final fee quote." }),
      result.fees.affordable === false && /* @__PURE__ */ jsx("p", { role: "alert", className: "text-destructive", children: "Insufficient native balance for value plus the estimated gas budget. Sending is blocked." })
    ] }),
    result.status === "success" && /* @__PURE__ */ jsxs(Fragment, { children: [
      !!result.permissionStates?.length && /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsx("h4", { className: "font-medium", children: "Permission state before and after" }),
        /* @__PURE__ */ jsx("ul", { className: "space-y-2", children: result.permissionStates.map((state, i) => /* @__PURE__ */ jsxs("li", { className: "rounded border p-3", children: [
          /* @__PURE__ */ jsx("p", { children: state.status === "verified" ? "State verified in simulation" : "State could not be verified" }),
          /* @__PURE__ */ jsxs("p", { children: [
            "Contract: ",
            /* @__PURE__ */ jsx(SafetyAddress, { address: state.permission.contract, chainId: result.request.chainId }),
            state.permission.tokenId != null && ` \xB7 NFT #${state.permission.tokenId}`
          ] }),
          /* @__PURE__ */ jsxs("p", { children: [
            "Spender: ",
            /* @__PURE__ */ jsx(SafetyAddress, { address: state.permission.spender, chainId: result.request.chainId })
          ] }),
          state.permission.token && /* @__PURE__ */ jsxs("p", { children: [
            "Token: ",
            /* @__PURE__ */ jsx(SafetyAddress, { address: state.permission.token, chainId: result.request.chainId })
          ] }),
          /* @__PURE__ */ jsxs("p", { children: [
            permissionValue(state.before),
            " \u2192 ",
            permissionValue(state.after)
          ] }),
          state.permission.kind === "nft" && /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsxs("p", { children: [
              "Token owner: ",
              state.before.tokenOwner ?? "unknown",
              " \u2192 ",
              state.after?.tokenOwner ?? "unknown"
            ] }),
            /* @__PURE__ */ jsx("p", { className: "text-xs", children: "Clearing this token approval does not revoke collection-wide operator permissions." })
          ] }),
          state.error && /* @__PURE__ */ jsx("p", { className: "text-xs", children: state.error })
        ] }, i)) })
      ] }),
      /* @__PURE__ */ jsx("h4", { className: "font-medium", children: "Sender balance changes" }),
      result.balanceChanges.length ? /* @__PURE__ */ jsx("ul", { className: "space-y-2", children: result.balanceChanges.map((change, i) => /* @__PURE__ */ jsxs("li", { className: "rounded border p-3", children: [
        /* @__PURE__ */ jsxs("p", { className: "font-medium", children: [
          change.change > 0n ? "+" : "",
          change.decimals == null ? `${change.change} base units` : formatUnits(change.change, change.decimals),
          " ",
          change.symbol ?? "asset"
        ] }),
        /* @__PURE__ */ jsx("p", { children: /* @__PURE__ */ jsx(SafetyAddress, { address: change.token, chainId: result.request.chainId }) }),
        /* @__PURE__ */ jsxs("p", { className: "text-xs text-muted-foreground", children: [
          "Before ",
          change.decimals == null ? change.before.toString() : formatUnits(change.before, change.decimals),
          " \u2192 after ",
          change.decimals == null ? change.after.toString() : formatUnits(change.after, change.decimals)
        ] })
      ] }, `${change.token}:${i}`)) }) : /* @__PURE__ */ jsx("p", { children: "No balance changes were returned. This does not establish that every asset was checked." }),
      /* @__PURE__ */ jsx("h4", { className: "font-medium", children: "Permission events emitted by the simulation" }),
      /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "These are contract-reported events. They can differ from actual permissions; use the verified state above when available." }),
      result.permissions.length ? /* @__PURE__ */ jsx("ul", { className: "space-y-2", children: result.permissions.map((p, i) => /* @__PURE__ */ jsx(PermissionRow, { permission: p, chainId: result.request.chainId }, i)) }) : /* @__PURE__ */ jsx("p", { children: "No recognized permission events were returned." })
    ] }),
    result.requestedPermissions.length > 0 && /* @__PURE__ */ jsxs("details", { children: [
      /* @__PURE__ */ jsx("summary", { children: "Requested permissions from calldata" }),
      /* @__PURE__ */ jsx("p", { className: "my-2 text-xs text-muted-foreground", children: "These describe the requested call, not a verified outcome. A matching selector does not prove the contract implements that standard; approve\u2019s numeric argument can also be an NFT ID." }),
      /* @__PURE__ */ jsx("ul", { className: "space-y-2", children: result.requestedPermissions.map((p, i) => /* @__PURE__ */ jsx(PermissionRow, { permission: p, chainId: result.request.chainId }, i)) })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "rounded border bg-muted/30 p-3", children: [
      /* @__PURE__ */ jsx("p", { className: "font-medium", children: "What this preview cannot establish" }),
      /* @__PURE__ */ jsx("ul", { className: "mt-1 list-disc space-y-1 pl-5 text-xs", children: result.limitations.map((note) => /* @__PURE__ */ jsx("li", { children: note }, note)) })
    ] }),
    /* @__PURE__ */ jsxs("details", { className: "text-xs", children: [
      /* @__PURE__ */ jsx("summary", { children: "Block reference" }),
      /* @__PURE__ */ jsx("p", { className: "break-all", children: result.blockHash })
    ] })
  ] });
}
function TransactionReview(props) {
  const source = React10.useMemo(() => props.source ?? createWalletSafetySource(), [props.source]);
  const [scope, setScope] = React10.useState({ source, revision: 0 });
  if (scope.source !== source) setScope({ source, revision: scope.revision + 1 });
  return /* @__PURE__ */ jsx(Review, { ...props, source }, `${previewRequestKey(props.request)}:${scope.revision}`);
}
function Review({ request, source: supplied, onSend, sendDisabledReason, busy = false, renderExplanation }) {
  const source = React10.useMemo(() => supplied ?? createWalletSafetySource(), [supplied]);
  const [result, setResult] = React10.useState(null);
  const [reading, setReading] = React10.useState(false);
  const [sending, setSending] = React10.useState(false);
  const [error, setError] = React10.useState(null);
  const [acceptUnavailable, setAcceptUnavailable] = React10.useState(false);
  const [now, setNow] = React10.useState(Date.now);
  const controller = React10.useRef(null);
  const sendLock = React10.useRef(false);
  const reviewRegion = React10.useRef(null);
  React10.useEffect(() => {
    reviewRegion.current?.focus();
  }, []);
  React10.useEffect(() => () => controller.current?.abort(), []);
  React10.useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1e3);
    return () => clearInterval(timer);
  }, []);
  const preview = async () => {
    controller.current?.abort();
    const attempt = new AbortController();
    controller.current = attempt;
    setReading(true);
    setError(null);
    setResult(null);
    setAcceptUnavailable(false);
    try {
      const next = await source.preview(request, attempt.signal);
      if (!attempt.signal.aborted) {
        setResult(next);
        setNow(Date.now());
      }
    } catch (e) {
      if (!attempt.signal.aborted) setError(safetyError(e));
    } finally {
      if (!attempt.signal.aborted) setReading(false);
    }
  };
  const send = async () => {
    if (!onSend || sendDisabledReason || busy || sendLock.current || !previewCanSend(result, request, Date.now(), acceptUnavailable)) return;
    sendLock.current = true;
    setSending(true);
    setError(null);
    try {
      await onSend(request, { preview: result, acceptUnavailable, signal: controller.current?.signal });
      setResult(null);
      setAcceptUnavailable(false);
    } catch (e) {
      setError(safetyError(e));
    } finally {
      sendLock.current = false;
      setSending(false);
    }
  };
  const meta = getChainMetadata(request.chainId);
  return /* @__PURE__ */ jsxs("section", { ref: reviewRegion, tabIndex: -1, "aria-label": "Review transaction", className: "space-y-3 rounded border p-4 text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring", children: [
    /* @__PURE__ */ jsxs("h3", { className: "font-semibold", children: [
      "Review transaction \xB7 ",
      chainDisplayName(request.chainId)
    ] }),
    /* @__PURE__ */ jsxs("p", { children: [
      "From: ",
      /* @__PURE__ */ jsx(SafetyAddress, { address: request.from, chainId: request.chainId })
    ] }),
    /* @__PURE__ */ jsx("p", { children: request.calls ? `Atomic bundle \xB7 ${request.calls.length} ordered calls` : request.to ? /* @__PURE__ */ jsxs(Fragment, { children: [
      "To: ",
      /* @__PURE__ */ jsx(SafetyAddress, { address: request.to, chainId: request.chainId })
    ] }) : "Create a new contract" }),
    request.calls && /* @__PURE__ */ jsx("ol", { className: "list-decimal space-y-2 pl-5", children: request.calls.map((call, i) => /* @__PURE__ */ jsxs("li", { children: [
      /* @__PURE__ */ jsx(SafetyAddress, { address: call.to, chainId: request.chainId }),
      /* @__PURE__ */ jsxs("p", { children: [
        call.value.toString(),
        " native base units"
      ] }),
      /* @__PURE__ */ jsxs("details", { children: [
        /* @__PURE__ */ jsx("summary", { children: "Call data" }),
        /* @__PURE__ */ jsx("pre", { className: "overflow-auto whitespace-pre-wrap break-all", children: call.data })
      ] })
    ] }, i)) }),
    /* @__PURE__ */ jsxs("p", { children: [
      request.calls ? "Total native value" : "Native value",
      ": ",
      formatUnits(request.calls?.reduce((sum, call) => sum + call.value, 0n) ?? request.value, meta?.decimals ?? 18),
      " ",
      meta?.nativeToken ?? "native"
    ] }),
    /* @__PURE__ */ jsxs("details", { children: [
      /* @__PURE__ */ jsx("summary", { children: "Exact calldata" }),
      /* @__PURE__ */ jsx("pre", { className: "max-h-40 overflow-auto whitespace-pre-wrap break-all text-xs", children: request.data })
    ] }),
    /* @__PURE__ */ jsx(Button, { type: "button", variant: "outline", disabled: reading || busy || sending, onClick: () => void preview(), children: reading ? "Simulating\u2026" : result ? "Refresh preview" : "Run preview" }),
    error && /* @__PURE__ */ jsx("p", { role: "alert", className: "break-words text-destructive", children: error }),
    result && /* @__PURE__ */ jsx(TransactionPreviewResult, { result }),
    result && renderExplanation?.(result),
    result && now - result.checkedAt >= PREVIEW_MAX_AGE_MS && /* @__PURE__ */ jsx("p", { role: "status", children: "This preview has expired. Refresh it before sending." }),
    onSend && /* @__PURE__ */ jsxs("div", { className: "space-y-2 border-t pt-3", children: [
      result?.status === "unavailable" && /* @__PURE__ */ jsxs("label", { className: "flex items-start gap-2 text-xs", children: [
        /* @__PURE__ */ jsx("input", { type: "checkbox", checked: acceptUnavailable, onChange: (e) => setAcceptUnavailable(e.target.checked) }),
        "I understand that the effects could not be simulated and want to review this transaction in my wallet."
      ] }),
      sendDisabledReason && /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: sendDisabledReason }),
      /* @__PURE__ */ jsx(Button, { type: "button", disabled: Boolean(sendDisabledReason) || reading || busy || sending || !previewCanSend(result, request, now, acceptUnavailable), onClick: () => void send(), children: busy || sending ? "Waiting for wallet\u2026" : "Review and send in wallet" }),
      /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "Your wallet asks for the final signature. Previewing does not sign or broadcast anything." })
    ] })
  ] });
}
var record = (value) => Boolean(value && typeof value === "object" && !Array.isArray(value));
var identifier = /^[A-Za-z_][A-Za-z0-9_]*$/;
var reserved = /* @__PURE__ */ new Set(["__proto__", "constructor", "prototype"]);
var domainTypes = { name: "string", version: "string", chainId: "uint256", verifyingContract: "address", salt: "bytes32" };
function exactTypedData(raw) {
  if (!record(raw) || !record(raw.domain) || !record(raw.types) || !record(raw.message) || typeof raw.primaryType !== "string") {
    throw new Error("Provide domain, types, primaryType and message.");
  }
  if (Object.keys(raw).some((key) => !["domain", "types", "primaryType", "message"].includes(key))) throw new Error("Typed data contains fields outside the signed structure.");
  if (raw.primaryType === "EIP712Domain") throw new Error("Domain-only signatures do not bind the displayed message.");
  const domain = { ...raw.domain };
  if (typeof domain.chainId !== "number" && typeof domain.chainId !== "string" || !/^(?:0x[0-9a-fA-F]+|\d+)$/.test(String(domain.chainId))) throw new Error("A valid signature domain chainId is required.");
  const chain = BigInt(domain.chainId);
  if (chain <= 0n || chain > BigInt(Number.MAX_SAFE_INTEGER)) throw new Error("The signature chainId is out of range.");
  domain.chainId = Number(chain);
  const types = {};
  if (Object.keys(raw.types).length > 64) throw new Error("Too many typed-data types.");
  for (const [name, fields] of Object.entries(raw.types)) {
    if (!identifier.test(name) || reserved.has(name) || !Array.isArray(fields) || fields.length > 128) throw new Error("Invalid typed-data schema.");
    const names = /* @__PURE__ */ new Set();
    types[name] = fields.map((field) => {
      if (!record(field) || Object.keys(field).some((key) => key !== "name" && key !== "type") || typeof field.name !== "string" || !identifier.test(field.name) || reserved.has(field.name) || names.has(field.name) || typeof field.type !== "string" || field.type.length > 128) throw new Error("Invalid or duplicate typed-data field.");
      names.add(field.name);
      return { name: field.name, type: field.type };
    });
  }
  types.EIP712Domain ??= Object.entries(domainTypes).filter(([name]) => Object.hasOwn(domain, name)).map(([name, type]) => ({ name, type }));
  if (!types.EIP712Domain.some((f) => f.name === "chainId" && f.type === "uint256") || !types.EIP712Domain.some((f) => f.name === "verifyingContract" && f.type === "address")) throw new Error("The domain schema must bind chainId and verifyingContract.");
  if (types.EIP712Domain.some((f) => domainTypes[f.name] !== f.type)) throw new Error("Unsupported signature domain schema.");
  let nodes = 0;
  const check = (type, value, path, depth = 0) => {
    if (++nodes > 4096 || depth > 32) throw new Error("Typed data exceeds the review complexity limit.");
    const array = /^(.*)\[(\d*)\]$/.exec(type);
    if (array) {
      if (!Array.isArray(value) || value.length > 1024 || array[2] !== "" && value.length !== Number(array[2])) throw new Error(`Invalid array at ${path}.`);
      for (const [i, item] of value.entries()) check(array[1], item, `${path}[${i}]`, depth + 1);
      return;
    }
    if (Object.hasOwn(types, type)) {
      if (!record(value)) throw new Error(`Expected signed fields at ${path}.`);
      const fields = types[type];
      if (Object.keys(value).some((key) => !fields.some((f) => f.name === key))) throw new Error(`Unsigned field at ${path}; every displayed field must be covered by its schema.`);
      for (const field of fields) {
        if (!Object.hasOwn(value, field.name)) throw new Error(`Missing signed field at ${path}.${field.name}.`);
        check(field.type, value[field.name], `${path}.${field.name}`, depth + 1);
      }
      return;
    }
    if (type === "bool" && typeof value === "boolean") return;
    if (type === "string" && typeof value === "string") return;
    if (type === "address" && typeof value === "string" && isAddress(value)) return;
    const bytes = /^bytes(\d*)$/.exec(type);
    if (bytes && typeof value === "string" && /^0x(?:[0-9a-fA-F]{2})*$/.test(value) && (bytes[1] === "" || Number(bytes[1]) >= 1 && Number(bytes[1]) <= 32 && value.length === 2 + Number(bytes[1]) * 2)) return;
    const integer = /^(u?int)(\d+)$/.exec(type);
    if (integer && (typeof value === "string" || typeof value === "number") && /^(?:-?\d+|0x[0-9a-fA-F]+)$/.test(String(value))) {
      const bits = Number(integer[2]);
      if (bits >= 8 && bits <= 256 && bits % 8 === 0) {
        const n = BigInt(value), signed = integer[1] === "int", limit = 1n << BigInt(bits - (signed ? 1 : 0));
        if (n >= (signed ? -limit : 0n) && n < limit) return;
      }
    }
    throw new Error(`Invalid ${type} value at ${path}.`);
  };
  check("EIP712Domain", domain, "domain");
  check(raw.primaryType, raw.message, "message");
  return { domain, types, primaryType: raw.primaryType, message: raw.message };
}

// src/safety/typedData.ts
var permitFields = "owner:address,spender:address,value:uint256,nonce:uint256,deadline:uint256";
var PERMIT_STATE_ABI = parseAbi(["function nonces(address owner) view returns (uint256)", "function DOMAIN_SEPARATOR() view returns (bytes32)"]);
function reviewTypedData(json, chainId, owner) {
  if (json.length > 131072) throw new Error("Typed data must be at most 128 KiB.");
  const raw = exactTypedData(JSON.parse(json, (_key, value) => {
    if (typeof value === "number" && !Number.isSafeInteger(value)) throw new Error("Use decimal strings for large typed-data integers.");
    return value;
  }));
  if (BigInt(raw.domain?.chainId ?? 0) !== BigInt(chainIdToNumber(chainId) ?? -1)) throw new Error("The signature domain must match the selected chain.");
  if (typeof raw.domain?.verifyingContract !== "string" || !isAddress(raw.domain.verifyingContract) || BigInt(raw.domain.verifyingContract) === 0n) throw new Error("A nonzero verifying contract is required.");
  const data = raw;
  const digest = hashTypedData(data);
  const review = { data, digest, owner, chainId };
  if (raw.primaryType === "Permit" && raw.types.Permit?.map((f) => `${f.name}:${f.type}`).join(",") === permitFields) {
    const message = raw.message;
    if (String(message.owner).toLowerCase() !== owner.toLowerCase()) throw new Error("The permit owner must match the assessed wallet.");
    review.permit = { spender: message.spender, amount: BigInt(message.value), nonce: BigInt(message.nonce), deadline: BigInt(message.deadline) };
  }
  return review;
}
async function checkTypedData(review, client) {
  if (await client.getChainId() !== chainIdToNumber(review.chainId)) throw new Error("The RPC returned a different signature network.");
  const head = await client.getBlock({ blockTag: "latest" });
  if (head.number == null || !head.hash) throw new Error("No mined signature assessment block is available.");
  if (review.permit) {
    if (review.permit.deadline < head.timestamp) throw new Error("This permit has expired.");
    const separator = await client.readContract({ address: review.data.domain.verifyingContract, abi: PERMIT_STATE_ABI, functionName: "DOMAIN_SEPARATOR", blockNumber: head.number });
    const expected = hashDomain({ domain: review.data.domain, types: { EIP712Domain: review.data.types.EIP712Domain ?? getTypesForEIP712Domain({ domain: review.data.domain }) } });
    if (separator.toLowerCase() !== expected.toLowerCase()) throw new Error("The signature domain differs from the token contract domain.");
    const nonce = await client.readContract({ address: review.data.domain.verifyingContract, abi: PERMIT_STATE_ABI, functionName: "nonces", args: [review.owner], blockNumber: head.number });
    if (nonce !== review.permit.nonce) throw new Error("The permit nonce no longer matches the contract.");
  }
  if ((await client.getBlock({ blockNumber: head.number })).hash !== head.hash) throw new Error("The signature assessment block changed. Review again.");
  return head;
}
function TypedDataReview({ chainId, owner, client: provided }) {
  const client = React10.useMemo(() => provided ?? createRpcPublicClients()(chainId), [provided, chainId]);
  const [scope, setScope] = React10.useState({ client, revision: 0 });
  if (scope.client !== client) setScope({ client, revision: scope.revision + 1 });
  return /* @__PURE__ */ jsx(SignatureReview, { chainId, owner, client }, `${chainId}:${owner.toLowerCase()}:${scope.revision}`);
}
function SignatureReview({ chainId, owner, client }) {
  const { data: wallet } = useWalletClient({ chainId: chainIdToNumber(chainId) });
  const [input, setInput] = React10.useState("");
  const [review, setReview] = React10.useState();
  const [checkedAt, setCheckedAt] = React10.useState(0);
  const [signature, setSignature] = React10.useState();
  const [error, setError] = React10.useState();
  const [busy, setBusy] = React10.useState(false);
  const [accepted, setAccepted] = React10.useState(false);
  const controller = React10.useRef(null);
  const lock = React10.useRef(false);
  React10.useEffect(() => () => controller.current?.abort(), []);
  const inspect = async () => {
    controller.current?.abort();
    const attempt = new AbortController();
    controller.current = attempt;
    setBusy(true);
    setError(void 0);
    setReview(void 0);
    setSignature(void 0);
    setAccepted(false);
    try {
      const value = reviewTypedData(input, chainId, owner);
      await checkTypedData(value, client);
      if (!attempt.signal.aborted) {
        setReview(value);
        setCheckedAt(Date.now());
      }
    } catch (e) {
      if (!attempt.signal.aborted) setError(safetyError(e));
    } finally {
      if (!attempt.signal.aborted) setBusy(false);
    }
  };
  const sign = async () => {
    if (!review || !wallet || !accepted || lock.current) return;
    lock.current = true;
    setBusy(true);
    setError(void 0);
    const attempt = controller.current;
    try {
      if (Date.now() - checkedAt >= 6e4) throw new Error("Refresh the signature review before signing.");
      await assertSigningAccount(wallet, chainIdToNumber(chainId), owner);
      await checkTypedData(review, client);
      attempt?.signal.throwIfAborted();
      await assertSigningAccount(wallet, chainIdToNumber(chainId), owner);
      attempt?.signal.throwIfAborted();
      if (Date.now() - checkedAt >= 6e4) throw new Error("The signature review expired.");
      const signed = await wallet.signTypedData({ ...review.data, account: wallet.account });
      attempt?.signal.throwIfAborted();
      if (!await client.verifyTypedData({ ...review.data, address: owner, signature: signed })) throw new Error("The signature could not be verified for this wallet.");
      attempt?.signal.throwIfAborted();
      setSignature(signed);
    } catch (e) {
      if (!attempt?.signal.aborted) setError(safetyError(e));
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };
  return /* @__PURE__ */ jsxs("section", { className: "space-y-3", "aria-label": "Typed data signature review", children: [
    /* @__PURE__ */ jsx("p", { className: "text-sm", children: "Review EIP-712 signatures and ERC-2612 permits. Signing can authorize asset transfers later without another wallet prompt. No signature is submitted to a relayer." }),
    /* @__PURE__ */ jsxs("label", { className: "block text-sm", children: [
      "Typed data JSON",
      /* @__PURE__ */ jsx("textarea", { "aria-label": "Typed data JSON", className: "block w-full rounded border bg-background p-2 font-mono text-xs", rows: 8, value: input, disabled: busy, onChange: (e) => {
        controller.current?.abort();
        setInput(e.target.value);
        setReview(void 0);
        setSignature(void 0);
      } })
    ] }),
    /* @__PURE__ */ jsx(Button, { variant: "outline", disabled: busy, onClick: () => void inspect(), children: busy ? "Checking\u2026" : "Review typed data" }),
    error && /* @__PURE__ */ jsx("p", { role: "alert", className: "text-sm text-destructive", children: error }),
    review && /* @__PURE__ */ jsxs("div", { className: "space-y-3 rounded border p-3 text-sm", children: [
      /* @__PURE__ */ jsxs("p", { children: [
        "Verifying contract: ",
        /* @__PURE__ */ jsx(SafetyAddress, { address: review.data.domain.verifyingContract, chainId })
      ] }),
      /* @__PURE__ */ jsxs("p", { children: [
        "Domain: ",
        review.data.domain?.name ?? "Unnamed",
        " \xB7 version ",
        review.data.domain?.version ?? "unspecified"
      ] }),
      review.permit ? /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsxs("p", { children: [
          "Spender: ",
          /* @__PURE__ */ jsx(SafetyAddress, { address: review.permit.spender, chainId })
        ] }),
        /* @__PURE__ */ jsxs("p", { children: [
          "Permission: ",
          review.permit.amount.toString(),
          " base units \xB7 nonce ",
          review.permit.nonce.toString(),
          " \xB7 deadline ",
          review.permit.deadline.toString()
        ] }),
        /* @__PURE__ */ jsx("p", { children: "Current nonce and deadline checked. Contract compliance and future execution remain unverified." })
      ] }) : /* @__PURE__ */ jsx("p", { children: "Unrecognized signature intent. Review every field; this may grant permissions not represented by an ERC-2612 allowance." }),
      /* @__PURE__ */ jsx("pre", { className: "max-h-72 overflow-auto whitespace-pre-wrap break-all text-xs", children: JSON.stringify(review.data, null, 2) }),
      /* @__PURE__ */ jsxs("p", { className: "break-all text-xs", children: [
        "Message digest: ",
        review.digest
      ] }),
      /* @__PURE__ */ jsxs("label", { className: "flex items-start gap-2", children: [
        /* @__PURE__ */ jsx("input", { type: "checkbox", checked: accepted, onChange: (e) => setAccepted(e.target.checked) }),
        "I understand this signature may authorize future actions and revoking an on-chain allowance may not invalidate it."
      ] }),
      /* @__PURE__ */ jsx(Button, { disabled: !accepted || !wallet || busy || Boolean(signature), onClick: () => void sign(), children: "Sign reviewed data in wallet" }),
      signature && /* @__PURE__ */ jsxs("details", { children: [
        /* @__PURE__ */ jsx("summary", { children: "Signature verified \xB7 keep private" }),
        /* @__PURE__ */ jsx("pre", { className: "whitespace-pre-wrap break-all text-xs", children: signature })
      ] })
    ] })
  ] });
}

export { APPROVAL_SCAN_BLOCKS, APPROVAL_SCAN_ROWS, ChainPicker, EvmWalletMenu, MAX_PREVIEW_DATA_BYTES, MAX_UINT160, PERMISSION_ABI, PERMIT_STATE_ABI, PREVIEW_MAX_AGE_MS, SafetyAddress, TransactionPreviewResult, TransactionReview, TypedDataReview, WEB3_DEFAULT_CHAIN_SLUG, WEB3_EVM_CONNECTION_ID, WEB3_PACKAGE, WEB3_SELECTED_CHAIN_KEY, WEB3_SIGNER_ROOT_ID, WEB3_SIGNER_TTL_MS, WalletMenu, WalletProvider, Web3Provider, approvalId, assertReviewedTransaction, autheEvmViemChains, blockOverride, candidateFromPermission, checkTypedData, clearDisconnected, createAutheWagmiConfig, createBitcoinAdapter, createContractReadSession, createInjectedEvmAdapter, createRpcContractReader, createSolanaAdapter, createWalletSafetySource, defaultWalletAdapters, isDisconnected, markDisconnected, permissionExpiry, permissionFromLog, prepareBundleRequest, preparePreviewRequest, previewCalls, previewCanSend, previewRequestKey, readApproval, readLogRange, recoverSigner, requestedPermissions, resolveRpcUrls, reviewTypedData, revocationRequest, rpcTransport, safetyError, stableArgsKey, toViemChain, useAddressInventory, useApprovalExposure, useAutheSigner, useContractRead, useContractReads, useContractWrite, useEvmRead, useEvmSignMessage, useEvmStorageAt, useSelectedChain, useWalletConnection, useWallets, validatePreview, verifyEvmSignature, writeOutcome };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map